import type {
	CapSeverity,
	LightningReportContinuingCurrentProbability,
	LightningReportDataset,
	NWSSeverity,
	WeatherHazardCategory,
} from '@xweather/protect-types'
import type { ValidatePhoneNumberLengthResult } from 'libphonenumber-js'
import type { AssetType } from '../../data/assets.ts'
import type { DeviceStatus } from '../../data/devices.ts'
import type { MeasurementTypeParameter, MeasurementTypeUnit, QualityReason } from '../../data/measurementTypes.ts'
import type { MissingMeasurementExplanationKey } from '../../data/missingMeasurements.ts'
import type { languages } from '../../data/userProfile.ts'
import type { ForecastRangeOption } from '../../data/xcast.ts'
import type { ParseFileError } from '../../features/assets/AssetFileUpload.tsx'
import type { ImpactSourceLabel } from '../../features/impacts/types.ts'
import type { StrikeDamagePotentialOption } from '../../features/reports/display-form/lightning/lightningReportUtils.ts'
import type { LightningEventColumnKey } from '../../features/reports/display-form/lightning/table/lightningEventColumnKeys.ts'
import type { ReportView } from '../../features/reports/display-form/reportViews.ts'
import type { ActionId, DeviceCheck, ReasonPart } from '../../features/serviceManagement/serviceMap/RecommendedActions.tsx'

export const EN = {
	translation: {
		title: 'Xweather Insight',
		apply: 'Apply',
		cancel: 'Cancel',
		clear: 'Clear',
		confirm: 'Confirm',
		back: 'Back',
		next: 'Next',
		edit: 'Edit',
		save: 'Save',
		saving: 'Saving...',
		deleting: 'Deleting...',
		delete: 'Delete',
		close: 'Close',
		expand: 'Expand',
		collapse: 'Collapse',
		latitude: 'Latitude',
		longitude: 'Longitude',
		add: 'Add',
		defaults: 'Default',
		uploadFile: 'Upload file',
		upload: 'Upload',
		readingFile: 'Reading file...',
		clearFileSelection: 'Clear file selection',
		fileSizeBytes: '{{size}} Bytes',
		fileSizeKB: '{{size}} KB',
		fileSizeMB: '{{size}} MB',
		less: 'Less',
		more: 'More',
		zoomIn: 'Zoom in',
		zoomOut: 'Zoom out',
		AND: 'AND',
		OR: 'OR',
		mapMode: {
			light: 'Light',
			dark: 'Dark',
			satellite: 'Satellite',
			satelliteToggle: 'Toggle to satellite mode',
			modeToggle: 'Toggle map mode',
		},
		yourSystemTime: 'Your system time',
		localTime: 'Local time',
		loading: 'Loading...',
		search: 'Search',
		searchPlaceholder: 'Search...',
		copy: 'Copy',
		actions: 'Actions',
		selectAll: 'Select all',
		deselectAll: 'Deselect all',
		goBack: 'Go back',
		collapsePanel: 'Hide panel',
		expandPanel: 'Show panel',
		help: 'Help',
		rootErrorBoundary: {
			title: 'Oops!',
			subtitle: "Something went wrong, and your request couldn't be completed.",
			reload: 'Reload',
		},
		snackbar: {
			routeNotFound: 'The page could not be found',
			myLocationError: 'Unable to retrieve your location',
			downloadFailure: 'Download failed',
		},
		timeline: {
			settings: 'Playback speed',
			repeatOn: 'Repeat on',
			repeatOff: 'Repeat off',
			now: 'NOW',
			nowTooltip: 'Click to lock timeline to NOW.',
			handleTooltipLocked: 'Timeline is locked to NOW.\nDrag to release.',
			dateRanges: {
				lastXhour: 'Last {{hours}} hours',
				betweenXhour: '± {{hours}} hours',
				nextXhour: 'Next {{hours}} hours',
				nextXday: 'Next {{days}} days',
			},
			dateRangeSelector: {
				title: 'Time range',
				autoUpdate: 'Auto-update timeline',
				doUpdate: 'Timeline will automatically update to maintain the selected duration. Beginning and end-times will change.',
				notUpdate:
					'Timeline will not automatically update. Beginning and end-times will be fixed to what is selected above, and won’t change.',
				disclaimer: 'Data is available from <b>{{startTime}}</b> up to <b>{{endTime}}</b>.',
				selectDuration: 'Select a preset duration',
				previousWeek: 'Previous week (24 h intervals)',
				reset: 'Reset',
			},
		},
		alerts: {
			add: 'Add alert',
			edit: 'Edit alerts',
			delete: 'Delete alert',
			new: 'New alert',
			cannotAdd: 'You have reached the maximum number of protected assets',
			alreadyProtected: 'This asset already has all possible alerts configured',
			editAlert: 'Edit alert',
			newAlertDisclaimer: 'Please note that newly created alerts can take up to 20 minutes to be fully activated and functional.',
			notFound: 'Alert not found',
			download30days: '30-day alerts',
			noActiveAlerts: 'No active alerts',
			noAlertsAdded: 'No alerts have been added yet.',
			alertsAt_one: 'alert',
			alertsAt_other: 'alerts',
			xLocations_one: 'location',
			xLocations_other: 'locations',
			stormETA: 'Storm ETA {{eta}} min',
			hailETA: 'Hail ETA {{eta}} min',
			alertsAtXLocations:
				'$t(alerts.alertsAt, {"count": {{alertCount}} }) at {{locationCount}} $t(alerts.xLocations, {"count": {{locationCount}} })',
			now: 'Now',
			realtimeAndExtremeSevere: 'Real-time or extreme/severe',
			forecastAndModerateMinor: 'Forecast or moderate/minor',
			type: {
				realtime: 'Real-time',
				forecast: 'Forecast',
			},
			alreadyConfigured: {
				realtime: 'Real-time configured',
				forecast: 'Forecast configured',
				both: 'Real-time & Forecast configured',
			},
			protectionType: {
				hail: 'Hail',
				lightning: 'Lightning',
				wind: 'Wind',
				measurement: 'Measurement',
				webRelay: 'Web relay',
				weatherHazard: 'Govt. Weather Service',
				precipitation: 'Precipitation',
			},
			parameterType: {
				realtime: 'Real-time',
				forecast: 'Forecast',
				both: 'Real-time & Forecast',
			},
			alertFor: '{{alert}} alert <br /> for {{locationName}}',
			rings: {
				alarm: 'Alarm',
				warning: 'Warning',
				info: 'Info',
			},
			measurement: {
				alertTooltipAbove: 'is above',
				alertTooltipBelow: 'is below',
				invalid: 'Invalid',
			},
			weatherHazard: {
				parameter: 'Parameter',
				capSeverity: {
					EXTREME: 'Extreme',
					SEVERE: 'Severe',
					MODERATE: 'Moderate',
					MINOR: 'Minor',
					UNKNOWN: 'Unknown',
					extremeSevere: 'Extreme / Severe',
					moderateMinor: 'Moderate / Minor',
				} satisfies Record<CapSeverity | 'extremeSevere' | 'moderateMinor', string>,
				nwsAlertSeverity: {
					WATCH: 'Watch',
					WARNING: 'Warning',
					ADVISORY: 'Advisory',
					STATEMENT: 'Statement',
					OTHER: 'Other',
				} satisfies Record<NWSSeverity, string>,
			},
			createSuccessful: '{{type}} alert created successfully',
			createFailure: 'Failed to create {{type}} alert',
			updateSuccessful: '{{type}} alert updated successfully',
			updateFailure: 'Failed to update {{type}} alert',
			deleteSuccessful: '{{type}} alert deleted successfully',
			deleteFailure: 'Failed to delete {{type}} alert',
			audibleAlertFail:
				'Audio notification failed to play. Please verify that you have given a browser permission to play sounds on the site.',
			audible: {
				silence: 'Turn OFF audio sound',
				silenced: 'Audible alert silenced for <b>{{locationName}}</b>',
				silencedProtection: '{{protectionType}} audible alert silenced for <b>{{locationName}}</b>',
			},
			form: {
				headerLabel: {
					hail: 'Hail',
					lightning: 'Lightning',
					wind: 'Wind',
					measurement: 'Measurement',
					webRelay: 'Web relay',
					weatherHazard: 'Governmental Weather Service alert',
					precipitation: 'Precipitation',
				},
				steps: {
					location: 'Asset',
					locationDescription: 'Where to monitor and track',
					alertParameter: 'Alert parameter',
					alertParameterDescription: 'Select a parameter',
					parameter: 'Parameter',
					alertConditions: 'Alert conditions',
					alertConditionsDescription: 'Define alert conditions',
					notifications: 'Notification lists',
					notificationsDescription: 'Select lists to notify',
					realtimeLightning: 'Real-time lightning',
					forecastHail: 'Forecast hail',
					forecastWind: 'Forecast wind',
					forecastPrecipitation: 'Forecast precipitation',
					forecastLightning: 'Forecast lightning',
					webRelay: 'Web relay configuration',
					webRelayDescription: 'define configuration',
					weatherHazard: 'Government weather service alerts',
					weatherHazardDescription: 'Define alert conditions',
				},
				locationStep: {
					subtitle: 'Select asset where to monitor and track from the list below',
					noAssetsFoundAddAssets: 'No assets found. Add assets to monitor and track them.',
				},
				parameterStep: {
					title: 'Alert parameter',
					subtitle: 'Select a parameter to monitor',
					subtitleMeasurement: 'Select what parameter to alert on based on real-time measurements from your devices.',
					noMeasurements:
						'No recent measurements are available from this device. Once it reports measurements again, you can create an alert.',
				},
				guidelinesStep: {
					realtimeTitle: 'Real-time guidelines',
					forecastTitle: 'Forecast guidelines',
					subtitle: 'Enter guidelines to include in the alert notification',
					label: 'Guidelines when conditions are met',
					requiredError: 'Guidelines are required',
					maxLimitError: 'Guidelines must be at most {{max}} characters',
				},
				notificationsStep: {
					realtimeTitle: 'Real-time alert notifications',
					forecastTitle: 'Forecast alert notifications',
					subtitle: 'Select user list to notify',
					realtimeNotificationListLabel: '{{distance}} {{unit}} • {{time}} min clear time',
					forecaseNotificationListLabel: '{{time}} min before storm',
					forecastHailNotificationListLabel: '≥ {{probability}}% probability within {{distance}} {{unit}} • {{time}} min clear time',
					forecastPrecipitationNotificationTitle: 'Forecast precipitation',
					forecastPrecipitationNotificationListLabel: '{{thresholds}}',
					disclaimerEmailAndSms:
						'All users will see active alert notifications in the browser application. Users on the selected user list will be notified by email and SMS according to their notification preferences.',
					disclaimerEmail:
						'All users will see active alert notifications in the browser application. Users on the selected user list will be notified by email according to their notification preferences.',
					addUserList: 'Add user list',
					noUsersNotified: 'No user list notified',
					clear: 'Clear',
					duplicateUserListError: 'No duplicate configurations allowed',
					notificationFrequency: 'Notification frequency',
					configureAdditionalUserlists: 'Configure additional lists in <b>Users and user lists</b>',
					PT15M: 'Every 15 min',
					PT1H: 'Every 1 hour',
					orSelectUserListsBelow: 'Or select user lists below',
				},
				measurements: {
					steps: {
						parameter: 'Measurement Alert',
						guidelines: 'Guidelines',
						parameterDescription: 'Define measurement alert conditions',
						guidelinesDescription: 'When conditions are met',
					},
					parameter: {
						title: 'Alert conditions',
						subtitle: 'Values for triggering alert',
						is: 'is',
						above: 'Above',
						below: 'Below',
						threshold: 'the threshold',
					},
				},
				realtime: {
					alertConditionsTitle: 'Real-time alert conditions',
				},
				forecast: {
					alertConditionsTitle: 'Forecast alert conditions',
					advanceTimeTitle: 'Forecast alert advance time',
					forecastHorizonLabel: 'Max alert lead time',
					advanceTimeSubtitle: 'Define how far in advance you want to receive an alert before the storm arrives (min)',
				},
				hail: {
					forecast: {
						alertConditionsSubtitle: 'Minimum probability of severe hail greater than 2.54 cm (1 in) in diameter',
						hailAvailabilityDisclaimer:
							'The alert will activate when severe hail is forecast to occur within the buffer radius around your asset. This feature is only available for assets in the United States, Canada, Japan, Australia, and Europe (excluding Greece and North Macedonia).',
						clearDisclaimer: 'The alert will clear {{time}} minutes after the last active hail alert.',
						probabilityOfSevereHail: 'Probability of severe hail',
						allHail: 'All hail alerts',
						moderateRiskHail: 'Moderate risk',
						highRiskHail: 'High risk',
						timeBeforeHail: '{{time}} min before severe hail',
						probabilityBelowError: 'Probability of severe hail must be greater than or equal to {{value}}%',
						probabilityAboveError: 'Probability of severe hail must be less than or equal to {{value}}%',
						bufferRadius: 'Buffer radius',
						bufferRadiusBelowError: 'Buffer radius must be greater than or equal to {{value}} {{unit}}',
						bufferRadiusAboveError: 'Buffer radius must be less than or equal to {{value}} {{unit}}',
					},
				},
				lightning: {
					realtime: {
						addRing: 'Add ring',
						removeRing: 'Remove ring',
						alertConditionsSubtitle:
							'Configure alerting at four distances from your asset. Specify clear times for each distance, from inside out: Alarm, Warning, Info 1, Info 2.',
						clearTime: 'Clear time',
						ringHeader: '{{ringName}} ring',
						ringHelperText: '{{range}} {{unit}}',
						ringRadius: '{{ringName}} radius',
						includeCloudToCloud: 'Include Cloud-to-Cloud lightning',
						cloudToGroundDisclaimer: 'Cloud-to-ground lightning is automatically included.',
						radiusBelowError: 'Radius must be greater than or equal to {{value}} {{unit}}',
						radiusAboveError: 'Radius must be less than or equal to {{value}} {{unit}}',
					},
				},
				wind: {
					forecast: {
						advanceTimeSubtitle: 'Define how far in advance you want to receive a wind forecast alert',
						forecastTimeHorizon: 'Forecast time horizon',
						frequency: 'Frequency',
						startTime: 'Start time',
						frequencyOption: 'Every {{interval}} h',
						onceADay: 'Once a day',
						windHeight: 'Wind height (above ground)',
						windSpeedAbove: 'Wind speed above',
						windGustAbove: 'Wind gust above',
						windSpeedDetails: 'Speeds of {{windSpeed}} {{unit}}',
						windGustDetails: 'Gusts of {{windGust}} {{unit}}',
						defaultDetails: 'Speeds of {{windSpeed}} {{unit}} {{operation}} gusts of {{windGust}} {{unit}}',
						windSpeedGustError: 'Either wind speed or wind gust must be enabled.',
						speedBelowError: 'Wind speed must be greater than or equal to {{value}} {{unit}}',
						speedAboveError: 'Wind speed must be less than or equal to {{value}} {{unit}}',
						gustBelowError: 'Wind gust must be greater than or equal to {{value}} {{unit}}',
						gustAboveError: 'Wind gust must be less than or equal to {{value}} {{unit}}',
						gustBelowSpeedError: 'Wind gust must be greater than wind speed',
						userListRequiredError: 'User list is required',
					},
				},
				precipitation: {
					forecast: {
						accumulationTitle: 'Precipitation accumulation',
						accumulationSubtitle:
							'Each threshold is checked against every hour of the forecast, using the precipitation expected over the window starting at that hour. An alert is raised as soon as any hour exceeds any threshold you set.',
						liquidOnlyExplainer:
							'Alerts are raised for liquid precipitation only. Forecast snow, sleet and other frozen precipitation will not trigger this alert.',
						fields: {
							accum1hr: '1-hour accumulation',
							accum6hr: '6-hour accumulation',
							accum24hr: '24-hour accumulation',
							accum72hr: '72-hour accumulation',
						},
						accumulationRequiredError: 'Select at least one accumulation threshold',
						thresholdBelowError: '{{label}} must be greater than or equal to {{value}} {{unit}}',
						thresholdAboveError: '{{label}} must be less than or equal to {{value}} {{unit}}',
					},
				},
				weatherHazard: {
					severity: 'Severity',
					selectSeverity: 'Select severities to be alerted on',
					allSeverities: 'All severities',
					extremeSevereOnly: 'Extreme / Severe only',
					extremeSevereDescription: 'Catastrophic / life-threatening / hazardous',
					moderateDescription: 'Hazardous event',
					minorDescription: 'Informational / monitoring',
					usCanada: 'US / Canada',
					europeGlobal: 'Global (incl. Europe)',
					parameters: {
						'911TelephoneOutage': '911 Telephone Outage',
						administrativeMessage: 'Administrative Message',
						airQualityAlert: 'Air Quality Alert',
						airStagnation: 'Air Stagnation',
						arcticOutflow: 'Arctic Outflow',
						ashFall: 'Ash Fall',
						avalanche: 'Avalanche',
						beachHazard: 'Beach Hazard',
						blizzard: 'Blizzard',
						blowingDust: 'Blowing Dust',
						blowingSnow: 'Blowing Snow',
						briskWind: 'Brisk Wind',
						blueAlert: 'Blue Alert',
						childAbductionEmergency: 'Child Abduction Emergency',
						civilDangerWarning: 'Civil Danger Warning',
						civialEmergencyMessage: 'Civil Emergency Message',
						coastalEvent: 'Coastal Event',
						coastalFlood: 'Coastal Flood',
						coldWeather: 'Cold Weather',
						denseFog: 'Dense Fog',
						denseSmoke: 'Dense Smoke',
						drought: 'Drought',
						dust: 'Dust',
						dustStorm: 'Dust Storm',
						earthQuake: 'Earthquake',
						evacuationImmediate: 'Evacuation - Immediate',
						extremeCold: 'Extreme Cold',
						extremeFireDanger: 'Extreme Fire Danger',
						extremeHeat: 'Extreme Heat',
						extremeWind: 'Extreme Wind',
						fire: 'Fire',
						fireWeather: 'Fire Weather',
						flashFlood: 'Flash Flood',
						flashFreeze: 'Flash Freeze',
						flood: 'Flood',
						fog: 'Fog',
						forestFire: 'Forest Fire',
						freeze: 'Freeze',
						freezingDrizzle: 'Freezing Drizzle',
						freezingFog: 'Freezing Fog',
						freezingRain: 'Freezing Rain',
						freezingSpray: 'Freezing Spray',
						frost: 'Frost',
						gale: 'Gale',
						hail: 'Hail',
						hazardousMaterials: 'Hazardous Materials',
						roadConditions: 'Hazardous Road Conditions',
						hazardousSeas: 'Hazardous Seas',
						hazardousWeatherOutlook: 'Hazardous Weather Outlook',
						heat: 'Heat',
						heavyFreezingSpray: 'Heavy Freezing Spray',
						heavySnow: 'Heavy Snow',
						highSurf: 'High Surf',
						highTemperature: 'High Temperature',
						highWind: 'High Wind',
						hurricane: 'Hurricane',
						hurricaneForceWind: 'Hurricane Force Wind',
						icePellets: 'Ice Pellets',
						iceStorm: 'Ice Storm',
						iceberg: 'Iceberg',
						lakeEffectSnow: 'Lake Effect Snow',
						lakeEffectAndBlowingSnow: 'Lake Effect And Blowing Snow',
						lakeShoreFlood: 'Lake Shore Flood',
						lakeWind: 'Lake Wind',
						lawEnforcement: 'Law Enforcement',
						lightning: 'Lightning',
						localAreaEmergency: 'Local Area Emergency',
						lowTemperature: 'Low Temperature',
						lowWater: 'Low Water',
						marineWeather: 'Marine Weather',
						nuclearPowerPlant: 'Nuclear Power Plant',
						radiologicalHazard: 'Radiological Hazard',
						rain: 'Rain',
						rainFall: 'Rain Fall',
						rainFlood: 'Rain Flood',
						redFlag: 'Red Flag',
						ripCurrent: 'Rip Current',
						severeThunderstorm: 'Severe Thunderstorm',
						sheepGrazing: 'Sheep Grazing',
						shelterInPlace: 'Shelter In Place',
						shortTermForecast: 'Short Term Forecast',
						smallCraft: 'Small Craft',
						smog: 'Smog',
						snowAndBlowingSnow: 'Snow And Blowing Snow',
						snowFall: 'Snow Fall',
						snowIce: 'Snow Or Ice',
						snowSquall: 'Snow Squall',
						specialMarine: 'Special Marine',
						specialWeather: 'Special Weather',
						squall: 'Squall',
						storm: 'Storm',
						stormSurge: 'Storm Surge',
						thunderstorm: 'Thunderstorm',
						tornado: 'Tornado',
						tropicalStorm: 'Tropical Storm',
						tropicalStormWind: 'Tropical Storm Wind',
						tsunami: 'Tsunami',
						typhoon: 'Typhoon',
						volcano: 'Volcano',
						volcanicActivity: 'Volcanic Activity',
						wind: 'Wind',
						winterStorm: 'Winter Storm',
						winterWeather: 'Winter Weather',
						unknown: 'Unknown',
					} satisfies Record<WeatherHazardCategory, string>,
					notification: {
						title: 'Notifications',
						subtitle: 'Select user lists to notify when alert conditions are met for the following severities:<br/>{{severities}}',
						allSeverities: 'All severities',
						userListsToNotify: 'User lists to notify',
						disclaimerWeatherHazardDetail: 'Users will only receive SMS notifications for alerts with extreme and severe severity levels.',
					},
				},
				threatzone: {
					disclaimerIntro: 'The map shows an <b>example</b> of a storm approaching your asset.',
					disclaimerHailDetail: 'The alert tells you the storm’s estimated time of arrival (ETA) at your asset.',
					lightningDisclaimerDetail: 'The alert tells you the storm’s estimated time of arrival (ETA) at your asset’s alarm ring.',
				},
			},
			alertsPanel: {
				alertConditions: 'Alert conditions',
				guidelines: 'Guidelines',
				notifications: 'Notifications',
				viewConfiguration: 'View alert settings',
				allClear: 'All clear',
				lastRead: 'Last read',
				error: 'Could not retrieve alerts',
				timeHorizon: 'Time horizon',
				noGuidelinesConfigured: 'No guidelines configured',
				deleteTitle: 'Delete {{locationName}} {{protectionType}} alert',
				deleteConfirmation: 'Are you sure you want to delete this alert? This action cannot be undone.',
				cannotDeleteAlertTitle: 'Cannot delete alert on {{locationName}}',
				cannotDeleteLightningWithWebRelay:
					'You cannot delete lightning alerts on an asset with an associated WebRelay device. Please contact <a href="mailto:support@xweather.com">Xweather Support</a> for assistance.',
				cannotEditLightningWithWebRelay:
					'You cannot edit lightning alerts on an asset with an associated WebRelay device. Please contact <a href="mailto:support@xweather.com">Xweather Support</a> for assistance.',
				closeAlertSettings: 'Close alert settings',
				clearTime: 'Clear time',
				beforeStorm: 'Before storm',
				bufferRadius: 'Buffer radius',
				notifyUserList: 'Notify user list(s)',
				notifFrequency: 'Notif. frequency',
				recommendationSummary: 'Recommendation summary',
				hail: {
					settings: 'Hail alert settings',
					probability: 'Prob. ≥{{prob}}%',
					minProbability: 'Min probability',
					stowDirection: 'Best stow dir.',
					solarFarmMessage:
						'The recommended stow direction is {{stowDirection}}, as the storm is currently located {{dirFromAsset}} from the asset and moving {{dirTo}}.',
				},
				lightning: {
					settings: 'Lightning alert settings',
					eta: 'ETA',
					horizon: 'Horizon',
					directionFromAsset: 'Direction from asset',
					forecastNotConfigured: 'Forecast alert not configured',
					data: 'Data',
					alertRadius: 'Alert Radius',
				},
				wind: {
					wind: 'Wind',
					gust: 'Gust',
					settings: 'Wind alert settings',
					windSpeed: 'Wind speed',
					windGust: 'Wind gust',
					windHeight: 'Wind height',
					threshold: 'Threshold',
					startingAt: 'starting {{time}}',
					aboveGround: 'above ground',
				},
				precipitation: {
					accumulation: 'Accumulation',
					startingAt: 'starting {{time}}',
					threshold: '≥{{value}}',
					pop: 'Prob. of precip.',
					accum1hr: '1 h accum.',
					accum6hr: '6 h accum.',
					accum24hr: '24 h accum.',
					accum72hr: '72 h accum.',
					windowShort: {
						accum1hr: '1 h',
						accum6hr: '6 h',
						accum24hr: '24 h',
						accum72hr: '72 h',
					},
					maxOf: 'Max {{window}}',
					windowBreached: '{{window}} ≥{{value}}',
				},
				measurement: {
					settings: 'Measurement alert settings',
					title: 'Measurement alert',
					settingsOnly: 'alert settings',
					threshold: 'Threshold',
					userList: 'User list',
					frequency: 'Frequency',
					noDuration: 'Frequency N/A',
				},
				weatherHazard: {
					parameter: 'Parameter',
					expiresIn: 'Expires in',
					statement: 'Statement from weather service',
				},
				webRelay: {
					settings: 'Web relay settings',
				},
			},
		},
		locations: {
			location: 'Location',
			locationsList: {
				status: 'Status',
				allLocations: 'All locations',
				favorites: 'Favorites',
				openMenu: 'Open locations menu',
				hideLocations: 'Hide assets with no configured alerts',
				showLocations: 'Show all assets',
				editFavorites: 'Edit favorites',
				editFavoritesDisclaimer: 'Select favorite location(s) to focus your map view and audio alerts.',
				editFavoritesError: 'Failed to update favorites.',
				addFavoriteError: 'Failed to add favorite.',
				removeFavoriteError: 'Failed to remove favorite.',
				tabs: {
					all: 'All ({{count}})',
					favorites: 'Favorites ({{count}})',
					alerting: 'Alerting ({{count}}/{{totalCount}})',
					noAlertSet: 'No alerts set ({{count}})',
				},
				favoritesDisclaimer: 'Favorites restrict your audio alerts and map view.',
				actions: {
					goToAsset: 'Go to asset',
					goToDevice: 'Go to device',
					addToFavorites: 'Add to favorites',
					removeFromFavorites: 'Remove from favorites',
				},
				menu: {
					viewAllLocations: 'View all locations',
					viewFavorites: 'View favorites',
				},
			},
			rings: {
				alarm: 'Alarm',
				warning: 'Warning',
				info: 'Info',
				clear: 'Clear',
			},
		},
		assets: {
			asset: 'Asset',
			add: 'New asset',
			edit: 'Edit asset',
			delete: 'Delete asset',
			cannotEdit: 'You cannot edit protected assets',
			totalAssets: '{{count}} assets',
			deleteTitle: 'Delete {{locationName}}',
			deleteConfirmation: 'Are you sure you want to delete this asset? This action cannot be undone.',
			notFound: 'Asset not found',
			welcome: 'Welcome to Xweather',
			getStarted: 'Get started by adding assets, alerts, and user lists to notify when severe weather threatens your operations.',
			searchLocation: 'Search location...',
			name: 'Asset name',
			type: 'Type',
			geometry: 'Geometry',
			description: 'Description',
			label: 'Label',
			createSuccessful: 'Asset created successfully',
			createFailure: 'Failed to create asset',
			updateSuccessful: 'Asset updated successfully',
			updateFailure: 'Failed to update asset',
			deleteSuccessful: 'Asset <i>{{locationName}}</i> deleted successfully',
			deleteFailure: 'Failed to delete asset <i>{{locationName}}</i>',
			panel: {
				noDescription: 'No description',
			},
			upload: {
				nameCharacterRange: '3-80 characters',
				descriptionCharacterRange: '0-300 characters',
				fileUploadDisclaimer:
					'You can optionally set the following properties in your GeoJSON to automatically pre-populate the form with asset properties.',
				dropFile: 'Drop your file here',
				dropFileOr: 'Drop your Geo JSON file here or',
				chooseFile: 'choose file',
				max: 'max',
				zipFileDisclaimer:
					'A valid ZIP file must contain exactly one .shp file. Optional associated <br/> shapefile files (such as .dbf and .prj) may also be included.',
				error: {
					invalidFile: 'Your file must be a valid .json, .geojson, .shp, or .zip file (max 10MB).',
					invalidFileNoShape: 'Your file must be a valid .json or .geojson file (max 10MB).',
					tooManyFiles: 'You can only upload one file at a time.',
					invalidGeoJson: 'Invalid GeoJSON format.',
					geometryMismatch:
						'The geometries of a feature collection must be of a single type: Point, MultiPoint, LineString, MultiLineString, Polygon, or MultiPolygon.',
					fileRead: 'There was a problem reading your file.',
					zipTooLarge: 'The uncompressed contents of this ZIP file exceed the 10MB limit.',
					areaTooLarge: 'The geometry of the file you provided exceeds the maximum allowed area of {{area}} {{unit}}².',
					verticesTooMany: 'The geometry of the file you provided exceeds the maximum allowed vertices of 500.',
					validationFailed: 'The geometry of the file you provided failed validation. Please try uploading your file again.',
				} satisfies Record<ParseFileError, string>,
			},
			detailsStep: {
				label: 'Asset details',
				description: 'Define asset details',
				title: 'Asset details',
				subtitle: 'Fill in asset information manually, or pre-populate the form via search or file upload.',
				assetName: 'Asset name',
				assetNameTooShortError: 'Asset name must be at least 3 characters',
				assetNameTooLongError: 'Asset name must be at most 80 characters',
				additionalDescription: 'Additional description',
				descriptionTooLongError: 'Description must be at most 300 characters',
				hubHeight: 'Hub height (m)',
				hubHeightHelperText: '10 - 200 m',
				hubHeightBelowError: 'Hub height must be at least 10 m',
				hubHeightAboveError: 'Hub height must be at most 200 m',
				hubHeightRequiredError: 'Hub height is required',
				maxMWCapacity: 'Max power (MW)',
				maxMWCapacityHelperText: '0 - 99,999 MW',
				maxMWCapacityBelowError: 'Max power must be at least 0 MW',
				maxMWCapacityAboveError: 'Max power must be at most 99,999 MW',
				maxMWCapacityRequiredError: 'Max power is required',
				powerCurve: 'Power curve',
				powerCurveRequiredError: 'Power curve is required',
				duplicateNameError: 'Asset name must be unique',
				assetTypeRequiredError: 'Asset type is required',
			},
			geometryStep: {
				label: 'Location',
				description: 'Define location and geometry',
				title: 'Asset location',
				subtitle: 'Add points or vertices on the map or enter coordinates below.',
				editingSubtitle: 'Add labels to the coordinates of your asset.',
				geometryDisclaimer:
					'An asset geometry cannot have more than 500 points and cannot exceed {{area}} {{unit}}² in area (including the maximum buffer distance of {{buffer}} {{unit}}).',
				areaTooLargeError: 'Asset area cannot exceed {{area}} {{unit}}².',
				verticesTooManyError: 'Asset cannot have more than 500 vertices.',
				validationFailedError: 'Geometry validation failed. Please try adding your geometry again.',
				geometryRequiredError: 'Asset geometry is required.',
				coordinateLabelTooLongError: 'Coordinate labels must be at most 80 characters.',
				duplicateCoordinateLabelError: 'Coordinate labels must be unique from one another and existing asset names.',
				plusMinusRange: '{{unit}} (±{{value}})',
				delete: 'Delete feature',
				deleteAll: 'Remove all features',
				point: 'Point(s)',
				line: 'Line(s)',
				polygon: 'Polygon(s)',
			},
			types: {
				AIRPORT: 'Airport',
				BUILDING: 'Building',
				CONST_SITE: 'Construction site',
				FARM: 'Farm',
				FIRE: 'Fire',
				FOREST: 'Forest',
				HELIPAD: 'Helipad',
				LAKE: 'Lake',
				MANUFACTURING: 'Manufacturing',
				MINE: 'Mine',
				NUCLEAR_FACILITY: 'Nuclear facility',
				OTHER: 'Other',
				PARK: 'Park',
				PORT: 'Port',
				POWER_PLANT: 'Power plant',
				RIVER: 'River',
				ROAD: 'Road',
				ROUTE: 'Route',
				RUNWAY: 'Runway',
				SOLAR_FARM: 'Solar farm',
				STADIUM: 'Stadium',
				SUBSTATION: 'Substation',
				TOWER: 'Tower',
				TRAIL: 'Trail',
				TRANSMISSION_LINE: 'Transmission line',
				TRANSMISSION_TERMINAL_STATION: 'Transmission terminal station',
				WIND_FARM: 'Wind farm',
				WIND_TURBINE: 'Wind turbine',
			} satisfies Record<AssetType, string>,
		},
		graph: {
			xCast: 'Xcast',
			noXCast: 'No Xcast available',
			timeRange: 'Time range',
			noData: 'No data available',
			now: 'NOW',
			alertLegend: 'Alerts',
			pickParameterToChart: 'Pick a parameter to chart',
			noXcast: 'No Xcast available for the selected parameters',
			infoRow: {
				timeAndDate: 'Time & date',
				scrollLeft: 'Scroll left',
				scrollRight: 'Scroll right',
				pickParameter: 'Pick parameter',
				removeParameter: 'Remove parameter',
				noParametersAvailable: 'No parameters available',
			},
			forecastRange: {
				OFF: 'Off',
				'6H': '6h',
				'24H': '24h',
				'72H': '72h',
				FULL: 'Full',
			} satisfies Record<ForecastRangeOption, string>,
		},
		csv: {
			downloadCSV: 'Download CSV',
			all: 'All',
			allMeasurements: 'All measurements',
			devices: 'Devices',
			sensors: 'Measurements from',
			startDate: 'Start date',
			endDate: 'End date',
			interval: 'Averaging for download',
			quality: 'Include measurements with quality',
			submit: 'Download CSV',
			interval0h: 'No averaging',
			interval1h: '1 h',
			interval6h: '6 h',
			interval24h: '24 h',
			qualityALL: 'All',
			qualityGOOD: 'Accepted only',
			qualityBAD: 'Rejected only',
			deviceIdRequired: 'At least one device must be selected',
			dateInFuture: 'Date cannot be in the future',
			error: 'CSV export timeout. Try selecting fewer measurements or a shorter time scale.',
			alertCSVError: 'Failed to create {{alertType}} CSV.',
			success: 'CSV created successfully.',
			generated: 'Generated',
			severity: 'Severity',
			assetName: 'Asset name',
			extent: 'Extent',
			firstEventTime: 'First event time',
			activeTime: 'Active time',
			lastEventTime: 'Last event time',
			clearTime: 'Clear time',
			duration: 'Duration (min)',
		},
		measurements: {
			download: 'Measurements',
			reportsLink: 'Measurements report',
			viewDevices: 'View devices',
			options: 'Options',
			addAlert: 'Add alert',
		},
		meteogram: {
			loadFailed: 'Failed to load weather data',
			retry: 'Retry',
			sunriseSunset: 'Sunrise / sunset',
			windGustValue: 'g:{{windGust}}',
			popValue: '{{pop}}%',
			rowLabels: {
				severeWeatherRisk: 'Severe weather risk',
				temperature: 'Temperature',
				feelsLike: 'Feels like',
				wind: 'Wind',
				windGust: 'Wind gust',
				precip: 'Precipitation',
				visibility: 'Visibility',
			},
			severeWeatherRisk: {
				NONE: 'Minimal',
				TORN: 'TORN',
				WIND: 'WIND',
				HAIL: 'HAIL',
				LTG: 'LTG',
			},
			cloudCodes: {
				CL: 'Clear',
				FW: 'Mostly clear',
				SC: 'Partly cloudy',
				BK: 'Mostly cloudy',
				OV: 'Overcast',
			},
			// biome-ignore format: conciseness
			conditions: {
				L: { VL: 'Very light drizzle', L: 'Light drizzle', '': 'Drizzle', H: 'Heavy drizzle', VH: 'Very heavy drizzle' },
				R: { VL: 'Very light rain', L: 'Light rain', '': 'Rain', H: 'Heavy rain', VH: 'Very heavy rain' },
				RW: { VL: 'Very light rain showers', L: 'Light rain showers', '': 'Rain showers', H: 'Heavy rain showers', VH: 'Very heavy rain showers' },
				A: { VL: 'Very light hail', L: 'Light hail', '': 'Hail', H: 'Heavy hail', VH: 'Very heavy hail' },
				IP: { VL: 'Very light sleet', L: 'Light sleet', '': 'Sleet', H: 'Heavy sleet', VH: 'Very heavy sleet' },
				S: { VL: 'Very light snow', L: 'Light snow', '': 'Snow', H: 'Heavy snow', VH: 'Very heavy snow' },
				SW: { VL: 'Very light snow showers', L: 'Light snow showers', '': 'Snow showers', H: 'Heavy snow showers', VH: 'Very heavy snow showers' },
				SI: { VL: 'Very light snow and sleet', L: 'Light snow and sleet', '': 'Snow and sleet', H: 'Heavy snow and sleet', VH: 'Very heavy snow and sleet' },
				RS: { VL: 'Very light rain and snow', L: 'Light rain and snow', '': 'Rain and snow', H: 'Heavy rain and snow', VH: 'Very heavy rain and snow' },
				WM: { VL: 'Very light wintry mix', L: 'Light wintry mix', '': 'Wintry mix', H: 'Heavy wintry mix', VH: 'Very heavy wintry mix' },
				T: { VL: 'Thunderstorms', L: 'Thunderstorms', '': 'Thunderstorms', H: 'Severe thunderstorms', VH: 'Severe thunderstorms' },
				ZL: { VL: 'Very light freezing drizzle', L: 'Light freezing drizzle', '': 'Freezing drizzle', H: 'Heavy freezing drizzle', VH: 'Very heavy freezing drizzle' },
				ZR: { VL: 'Very light freezing rain', L: 'Light freezing rain', '': 'Freezing rain', H: 'Heavy freezing rain', VH: 'Very heavy freezing rain' },
				ZY: { VL: 'Freezing spray', L: 'Freezing spray', '': 'Freezing spray', H: 'Heavy freezing spray', VH: 'Heavy freezing spray' },
				ZF: { VL: 'Freezing fog', L: 'Freezing fog', '': 'Freezing fog', H: 'Freezing fog', VH: 'Freezing fog' },
				IC: { VL: 'Ice crystals', L: 'Ice crystals', '': 'Ice crystals', H: 'Ice crystals', VH: 'Ice crystals' },
				IF: { VL: 'Ice fog', L: 'Ice fog', '': 'Ice fog', H: 'Ice fog', VH: 'Ice fog' },
				BR: { VL: 'Mist', L: 'Mist', '': 'Mist', H: 'Mist', VH: 'Mist' },
				F: { VL: 'Fog', L: 'Fog', '': 'Fog', H: 'Dense fog', VH: 'Dense fog' },
				H: { VL: 'Haze', L: 'Haze', '': 'Haze', H: 'Haze', VH: 'Haze' },
				K: { VL: 'Smoke', L: 'Smoke', '': 'Smoke', H: 'Thick smoke', VH: 'Thick smoke' },
				BD: { VL: 'Blowing dust', L: 'Blowing dust', '': 'Blowing dust', H: 'Heavy blowing dust', VH: 'Heavy blowing dust' },
				BN: { VL: 'Blowing sand', L: 'Blowing sand', '': 'Blowing sand', H: 'Heavy blowing sand', VH: 'Heavy blowing sand' },
				BS: { VL: 'Blowing snow', L: 'Blowing snow', '': 'Blowing snow', H: 'Heavy blowing snow', VH: 'Heavy blowing snow' },
				BY: { VL: 'Blowing spray', L: 'Blowing spray', '': 'Blowing spray', H: 'Heavy blowing spray', VH: 'Heavy blowing spray' },
				FR: { VL: 'Frost', L: 'Frost', '': 'Frost', H: 'Frost', VH: 'Frost' },
				FC: { VL: 'Funnel cloud', L: 'Funnel cloud', '': 'Funnel cloud', H: 'Funnel cloud', VH: 'Funnel cloud' },
				TO: { VL: 'Tornado', L: 'Tornado', '': 'Tornado', H: 'Tornado', VH: 'Tornado' },
				WP: { VL: 'Waterspouts', L: 'Waterspouts', '': 'Waterspouts', H: 'Waterspouts', VH: 'Waterspouts' },
				VA: { VL: 'Volcanic ash', L: 'Volcanic ash', '': 'Volcanic ash', H: 'Volcanic ash', VH: 'Volcanic ash' },
				UP: { VL: 'Unknown precipitation',L: 'Unknown precipitation','': 'Unknown precipitation',H: 'Unknown precipitation',VH: 'Unknown precipitation' },
			},
		},
		devices: {
			addDevice: 'Add device',
			batteryLife: 'Battery life',
			batteryVoltage: 'Battery voltage',
			oldMetricTooltip: 'Reported value is over 2 hours old.',
			component: 'Component',
			components: 'Components',
			delete: 'Delete device',
			deleted: 'Device deleted',
			deleteError: 'Failed to remove device',
			deleteConfirmation: 'Are you sure you want to delete device {{device.name}}?',
			viewDetails: 'View device details',
			restartSuccess: 'Restart request sent for {{name}}',
			restartError: 'Failed to send restart request for {{name}}',
			device: 'Device',
			deviceNotFound: 'Device not found',
			editDevice: 'Edit device',
			elevation: 'Elevation',
			firmware: 'Firmware',
			serialNumber: 'Serial number',
			health: 'Health',
			id: 'ID',
			copyId: 'Copy ID',
			lastConnected: 'Last connected',
			lastReported: 'Last reported',
			accumulationResetTime: 'Daily precipitation reset time',
			inUtc: 'in UTC',
			location: 'Location',
			name: 'Device name',
			sensorName: 'name',
			restart: 'Restart',
			status: 'Status',
			deviceStatus: 'Device status',
			model: 'Model',
			totalDevices_one: '{{count}} device',
			totalDevices_other: '{{count}} devices',
			viewMeasurements: 'View measurements',
			devicesLimitReached:
				'You have reached the maximum number of devices entitled in your subscription. Please contact Vaisala sales for further advice.',
			statuses: {
				loading: 'Waiting for connection',
				OK: 'Device is online and sending data',
				DEGRADED: 'Last 10 measurements contain suspicious values',
				ERROR: 'New measurements have not been received within expected time limits',
				OFFLINE: 'Device is not connected to cloud',
				UNKNOWN: 'Device status is unknown',
			} satisfies Record<DeviceStatus['status'] | 'loading', string>,
			statusLabels: {
				OK: 'Online',
				DEGRADED: 'Quality issue',
				ERROR: 'Error',
				OFFLINE: 'Offline',
				UNKNOWN: 'Unknown',
			} satisfies Record<DeviceStatus['status'], string>,
			form: {
				newDevice: 'New device',
				deviceType: 'Device type',
				serialNumber: 'Serial number',
				name: 'Display name (optional)',
				location: 'Device location and precipitation settings',
				locationDescription: 'Click on the map or enter coordinates below.',
				latitude: 'Latitude',
				longitude: 'Longitude',
				altitude: 'Altitude',
				accumulationResetTime: 'Daily precipitation reset time in UTC (optional)',
				accumulationResetTimeLocal: 'Your preferred time zone is {{timezone}}, so the reset time is currently {{localResetTime}}.',
				accumulationResetTimeUtc: 'Your preferred time zone is UTC, so the reset time is also {{resetTime}}.',
				accumulationResetTimeInfo:
					'Daily precipitation accumulation will reset at <b>{{resetTime}} UTC</b> every day. This time remains fixed and does not adjust for daylight saving time.',
				mapMarkerName: 'New device',
				stepLabel: 'Device',
				stepDescriptionNew: 'Define device details',
				stepDescriptionEdit: 'Edit device details',
				errors: {
					'404': 'Device not found. Please try again.',
					'403':
						'Device already added to a different account. Check the device serial number or contact <a href="mailto:support@xweather.com">support@xweather.com</a>',
					'409': 'Device already added.',
					'401': 'Could not add device. Authorization failed.',
					'400': 'Could not add device. Invalid argument(s).',
					unknown: 'Could not add device. Please try again or contact support.',
					accumulationResetTime: 'Failed to save daily precipitation reset time.',
					accumulationResetTimeNewDevice: 'Failed to save daily precipitation reset time. Try editing the device to set it.',
				},
				success: {
					register: 'Device registered successfully.',
					update: 'Device updated successfully.',
				},
			},
			linearCorrection: {
				linearCorrection: 'Linear correction',
				measurement: 'Measurement',
				gain: 'Gain',
				offset: 'Offset',
				showDetails: 'Show details',
				hideDetails: 'Hide details',
				saveButton: 'Save linear correction settings',
				tooltip: 'Correction function is gain * value + offset',
				success: 'Linear correction settings saved.',
			},
		},
		legend: {
			captions: {
				alerts: {
					alert: 'Alert',
					expireDate: 'Expiry time',
				},
				fires: {
					new: 'New',
					active: 'Active',
					contained: 'Contained',
					name: 'Name',
					extent: 'Extent',
				},
				hail: {
					eta: 'ETA',
					probSevere: 'Probability of Severe Hail',
					slightRisk: 'Slight risk (≥0%)',
					moderateRisk: 'Moderate risk ({{num}}%)',
					highRisk: 'High risk ({{num}}%)',
					dirTo: 'Direction To',
				},
				lightning: {
					cloudToGround: 'Cloud-to-ground',
					cloudToCloud: 'Cloud-to-cloud',
					eta: 'ETA',
					strength: 'Strength',
					type: 'Type',
					time: 'Time',
					age: 'Age',
					density: 'Density',
					eventTypeTitle: 'Lightning event type',
					negativeCloudToGround: 'Negative cloud-to-ground',
					positiveCloudToGround: 'Positive cloud-to-ground',
					distanceFromAsset: 'Distance from asset',
					distanceFromCustomPoint: 'Distance from custom point',
					distanceFromClosestAsset: 'Distance from the closest asset(s)',
				},
				radar: {
					radarRain: 'Radar (rain)',
					radarMix: 'Radar (mix)',
					radarSnow: 'Radar (snow)',
				},
				road_weather_summary: {
					bad: 'BAD',
					fair: 'FAIR',
					good: 'GOOD',
				},
				'tropical-cyclones': {
					low: 'Low',
					depression: 'Depression',
					storm: 'Storm',
					cat1: 'Cat 1',
					cat2: 'Cat 2',
					cat3: 'Cat 3',
					cat4: 'Cat 4',
					cat5: 'Cat 5',
					typhoon: 'Typhoon',
					superTyphoon: 'Super Typhoon',
					name: 'Name',
					type: 'Type',
					eta: 'ETA',
				},
			},
			inputs: {
				lightning: {
					all: 'All events',
					cloudToGround: 'CG only',
					last30Min: 'Last 30 minutes',
					last60Min: 'Last 60 minutes',
				},
			},
			tooltip: {
				layerAnimate: 'Layer is animated',
				layerInanimate: 'Layer is not animated',
			},
			settings: {
				baseMapTitle: 'Base map settings',
				mapMode: 'Map mode',
			},
		},
		layers: {
			title: 'Map layers',
			titleLegend: 'Map legends',
			titleSettings: 'Map settings',
			done: 'Done',
			views: {
				'air-quality-index': 'Air quality index',
				alerts: 'Governmental alerts and advisories',
				'cloud-cover': 'Cloud cover',
				'dew-points': 'Dew points',
				extreme: 'Other extreme phenomena',
				'feels-like': 'Feels like',
				'hail-threats': 'Severe hail forecast',
				'lightning-alert-rings': 'Lightning alert rings',
				'lightning-density': 'Lightning density • 2016-2025',
				'lightning-realtime': 'Lightning event time',
				'lightning-threatzone': 'Lightning forecast',
				'power-lines': 'Power lines',
				'power-lines-only': 'Lines only',
				'power-lines-ltg': 'Lines with lightning density',
				precip: 'Precipitation',
				'pressure-msl': 'Surface pressure',
				radar: 'Radar',
				'road-temperature': 'Road temperature',
				'road-weather-summary': 'Road weather summary',
				'satellite-infrared-color': 'Satellite infrared color',
				'snow-depth': 'Snow depth',
				snow: '1-h Snowfall',
				storm: 'Storm',
				'temperature-contour': 'Temperature contour',
				temperature: 'Temperature',
				'tropical-cyclones': 'Tropical cyclones',
				visibility: 'Visibility',
				fires: 'Wild fires',
				'wind-barbs': 'Wind barbs',
				'wind-gusts': 'Wind gust',
				wind: 'Wind',
				gustIndicator: 'g',
			},
			groups: {
				airQuality: 'Air quality',
				general: 'General weather',
				road: 'Road weather',
				severe: 'Severe weather',
				snow: 'Snow',
				solarEnergy: 'Solar energy',
				wind: 'Wind',
				windEnergy: 'Wind energy',
				climatology: 'Climatology',
			},
		},
		mapTools: {
			distanceTool: {
				distanceTool: 'Distance tool <code>(Ctrl + D)</code>',
				clickToStartMeasuringDistance: 'Click to start measuring distance',
				clearDistance: 'Clear',
			},
			showMyLocation: 'Show my location',
		},
		mapTooltip: {
			lightning: 'Lightning',
			lightningForecast: 'Lightning Forecast',
			hail: 'Severe Hail',
			fire: 'Fire',
			cyclone: 'Cyclone',
			govtAlerts: 'Govt. Alerts',
		},
		windDownscaler: {
			create: 'Create 3D wind',
		},
		impacts: {
			allRiskAnalysis: 'All Risk analysis',
			favorites: 'Favorites',
			day: 'Day',
			next24Hours: 'Next 24 hours',
			next7Days: 'Next 7 days',
			download: 'Download',
			periodFilter: 'Time period',
			riskLevelFilter: 'Filter by risk level',
			filterParameters: 'Filter parameters',
			allParameters: 'All weather parameters',
			noParameters: 'No weather parameters',
			sortBySlot: 'Sort by impact at {{slot}}',
			sortBySlotWorstFirst: 'Sorted by impact at {{slot}}, worst first',
			sortBySlotBestFirst: 'Sorted by impact at {{slot}}, best first',
			major: 'Major',
			high: 'High',
			moderate: 'Moderate',
			low: 'Low',
			minimal: 'Minimal',
			addToFavorites: 'Add to favorites',
			removeFromFavorites: 'Remove from favorites',
			riskAnalysis: 'Risk analysis',
			risks: 'Risks',
			now: 'Now {{when}}',
			today: 'Today',
			parameterComparison: 'Parameter comparison',
			impactsAndSignals: 'Impact & Signals',
			time: 'Time',
			best: 'Best',
			worst: 'Worst',
			impact: 'Impact',
			signals: 'Signals',
			lastRefreshed: 'Last refreshed',
			generated: 'Generated',
			timezone: 'Time zone',
			assetUtcOffset: "Asset's UTC{{offset}}",
			riskLevels: 'Risk levels',
			parameters: 'Weather parameters',
			cumulativeLane: 'All impacts',
			forecastSummary: 'Forecast summary',
			summaryUnavailable: 'No summary available for this location right now',
			rules: {
				lightning: 'Lightning / Convection',
				wind: 'Wind',
				rainfall: 'Rainfall (24 h)',
				snowfall: 'Snowfall (24 h)',
				precipRate: 'Precipitation rate (1 h)',
				temperature: 'Temperature',
				extremeHeat: 'Extreme heat',
				extremeCold: 'Extreme cold',
				tropicalCyclones: 'Tropical cyclones',
			},
			sources: {
				lightningThreats: 'Lightning threats',
				governmentAlerts: 'Government Alerts',
				thunderstormPotential: 'Thunderstorm potential',
				convectiveOutlook: 'Convective outlook',
				windSpeed: 'Wind speed',
				windGusts: 'Wind gusts',
				highWindOutlook: 'High wind outlook',
				accumulatedPrecipitation: 'Accumulated precipitation',
				precipitationRate: 'Precipitation rate',
				accumulatedSnowfall: 'Accumulated snowfall',
				temperature: 'Temperature',
				feelsLike: 'Feels like',
				wetBulbGlobeTemperature: 'Wet-bulb globe temperature',
				tropicalCyclonesRisk: 'Tropical cyclones risk',
			} satisfies Record<ImpactSourceLabel, string>,
			indexUnit: 'index',
			noData: 'No data',
			noAlertsAdded: 'No alerts have been added to any of your assets.',
			noFavorites: 'No favorite assets found',
			searchAssets: 'Search assets...',
			noSearchResults: 'No assets match your search',
			unprotectedAssetsHidden: '<b>Assets with no alerts set up</b> are not shown in this list.',
			scoresUnavailable: 'Scores unavailable',
		},
		navigation: {
			menuTitle: 'Menu',
			dashboard: 'Dashboard',
			devices: 'Devices',
			apiDocumentation: 'API documentation',
			map: 'Map',
			measurements: 'Measurements',
			charts: 'Charts',
			weather: 'Weather',
			details: 'Details',
			playIntroVideo: 'Play intro video',
			preferences: 'Preferences',
			subscription: 'Subscription',
			webhooks: 'Webhooks',
			webRelays: 'Web relays',
			accounts: 'Accounts',
			serviceMap: 'Service map',
			tutorials: 'Tutorials',
			users: 'Users and user lists',
			apiKeys: 'API keys',
			logout: 'Logout',
			alerts: 'Alerts',
			reports: 'Reports',
			locations: 'Locations',
			alertHistory: 'Alert history',
			helpCenter: 'Help center',
			support: 'Contact support',
			add: {
				button: 'Add',
				asset: 'Asset',
				device: 'Device',
				alert: 'Alert',
				user: 'User',
				userList: 'User list',
				lightningAnalysisReport: 'Analysis report',
			},
			download: 'Download',
		},
		preferences: {
			title: 'User preferences',
			profile: 'Profile',
			name: 'Name',
			role: 'Role',
			unverified: 'Unverified',
			emailVerification: {
				sent: 'Verification email sent',
				failure: 'Failed to send email verification',
			},
			phone: 'Phone',
			phoneValidation: {
				TOO_LONG: 'Phone number is too long',
				TOO_SHORT: 'Phone number is too short',
				INVALID_COUNTRY: 'Invalid country code. Please select a valid one from the list',
				NOT_A_NUMBER: 'Invalid phone number',
				INVALID_LENGTH: 'Invalid phone number',
			} satisfies Record<ValidatePhoneNumberLengthResult, string>,
			phoneVerification: {
				notVerified: 'The phone number is not verified',
				verify: 'Verify',
				codeLabel: 'Enter the code sent to your phone',
				codePlaceholder: 'Enter code',
				codeButton: 'Verify code',
				phoneVerified: 'Phone number verified',
				failed: 'Wrong code entered, phone number can not be verified',
				removePhone: 'Remove number',
				phoneRemoved: 'Phone number removed',
			},
			notifications: 'Notifications',
			units: 'Units',
			airQualityIndex: 'Air quality index',
			regionalPreferences: 'Regional preferences',
			appearance: 'Appearance',
			notification: {
				audio: 'Browser notifications (audio)',
				audioLightningRings: 'Lightning rings',
				audioLightningRingLabels: {
					ALARM_AND_WARNING: 'Alarm and warning rings',
					ALL_RINGS: 'All rings',
				},
				email: 'Email notifications',
				sms: 'SMS notifications',
				snoozeSms: 'Snooze SMS notifications',
				startSnooze: 'Start snooze',
				endSnooze: 'End snooze',
			},
			unit: {
				unitsAirTemperature: 'Temperatures',
				unitsAirPressure: 'Air pressure',
				unitsDistance: 'Distance',
				unitsRain: 'Rain',
				unitsWindSpeed: 'Wind speed',
				unitsOtherGases: 'Gases (except CO<sub>2</sub>)',
				unitsAirQuality: 'Calculation and scale',
				theme: 'Interface and map theme',
			},
			unitLabels: {
				KILOMETRES: 'km',
				METRES: 'm',
				MILES: 'mi',
				NAUTICAL_MILES: 'nmi',
				DEGREES_CELSIUS: '°C',
				DEGREES_FAHRENHEIT: '°F',
				METRES_PER_SECOND: 'm/s',
				MILES_PER_HOUR: 'mph',
				KILOMETRES_PER_HOUR: 'km/h',
				KNOTS: 'knots',
				HECTOPASCALS: 'hPa',
				MILLIBARS: 'mbar',
				INCH_OF_MERCURY: 'inHg',
				MILLIMETRES: 'mm, mm/h',
				INCHES: 'in, in/h',
				PARTS_PER_BILLION: 'ppb',
				PARTS_PER_MILLION: 'ppm',
				MICROGRAMS_PER_CUBIC_METRE: 'μg/m<sup>3</sup>',
				EEA: 'EEA',
				USAQI: 'US AQI',
				LIGHT: 'Light',
				DARK: 'Dark',
				SYSTEM: 'System',
			},
			dateFormat: 'Date format',
			dateFormats: {
				DAY_MONTH_YEAR: 'DD.MM.YYYY',
				MONTH_DAY_YEAR: 'MM.DD.YYYY',
			},
			language: 'Language',
			languages: {
				EN: 'English',
				ES: 'Spanish',
				'pt-BR': 'Portuguese (Brazil)',
			} satisfies Record<(typeof languages)[number], string>,
			timezone: 'Time zone',
			timezoneAutodetect: 'Autodetect',
			timezoneSameAs: '{{city}} uses the same time zone as {{mainCity}}.',
		},
		reports: {
			add: 'New report',
			featureNotAvailableForSubscription:
				'This feature is not available for your subscription. Upgrade to Analyze Premium to access this feature.',
			featureNotAvailableForReport: 'Report was created without this feature enabled',
			analysisReport: 'Analysis report',
			form: {
				noAssets: 'No assets with configured alerts found',
				visibleAssetsDisclaimer: 'Only assets with configured alerts are visible in the list. Others are hidden.',
				radius: 'Radius',
				customPoint: 'Custom point-location within asset for analysis',
				customPointSingleAsset: 'Custom analysis point within a single selected asset',
				customPointOutsideAsset: 'Selected point-location is outside asset area',
				radiusAppliedToEverySelectedAsset: 'applied to every selected asset',
				assetTypeFilter: 'Asset type',
				allAssetTypes: 'All asset types',
				noMatchingAssets: 'No matching assets',
				mapSelection: {
					selectedCount_one: '{{count}} selected',
					selectedCount_other: '{{count}} selected',
					clearAll: 'Clear all',
					clickToSelect: 'Click to select',
					dragToSelect: 'Drag to select',
					clickHint: '<b>Click an asset</b> to add it to or remove it from the selection.',
					dragHint: '<b>Drag a rectangle</b> to select all assets within it.',
					addAssets_one: '<b>Add {{count}} asset</b> to selection',
					addAssets_other: '<b>Add {{count}} assets</b> to selection',
				},
				maxDistanceBetweenAssets: 'The selected assets must be within {{distance}} of each other.',
				timeRangeEndBeforeStart: 'End time must be after start time',
				localTime: 'Local time',
				timezone: 'Time zone',
				name: 'Report name',
				startDateTime: 'Start date-time',
				endDateTime: 'End date-time',
				succeededToCreateReport: 'Analysis report created successfully.',
				failedToCreateReport: 'Failed to create analysis report. Try again later.',
				geometryTooComplex:
					'Geometry of the selected asset(s) is too complex to create a report. Try to increase radius or simplify the asset geometry.',
				areaMaxExceeded: 'Report area must be at most {{maxArea, number}} {{unit}}²',
				dataAvailability: 'Available from <b>{{startDateTime}}</b> up to <b>{{endDateTime}}</b>',
				dateOutsideDataAvailability: 'Selected date is outside of data availability',
				loadingDataAvailability: 'Loading data availability...',
				errorLoadingDataAvailability: 'Error loading data availability for analyzing. Please try again later.',
				reportTypes: {
					lightning: 'Lightning',
					hail: 'Hail',
				},
				steps: {
					reportType: {
						label: 'Report type',
						shortDescription: 'What to analyze',
						longDescription: 'Select the type of report to create',
					},
					areaOfInterest: {
						label: 'Area of interest',
						shortDescription: 'What and where to analyze',
						longDescription: 'Select a point of interest and define radius to analyze',
					},
					timeRange: {
						label: 'Time range',
						shortDescription: 'When data is relevant',
						longDescription: 'Select a date or date range, along with the time and time zone.',
					},
					reportDetails: {
						label: 'Report details',
						shortDescription: 'Name the report',
						longDescription: 'Name the report',
					},
				},
			},
			table: {
				nameLocation: 'Name / Location',
				statusType: 'Status / Type',
				timeRange: 'Time range with time zone',
				parameterAndDataIncluded: 'Parameter and data included',
				dateCreated: 'Date created',
				removedAsset: 'Removed asset',
				noReportsFound: 'No reports found.',
				noReportsMatchSearch: 'No matching reports found.',
				nameDescription: '{{radiusWithUnit}} around {{assetName}}{{formattedCustomPoint}}',
				nameDescriptionOfSeveralLocations: '{{radiusWithUnit}} around each: {{assetNames}}',
				analysis: 'Analysis',
				statusProcessing: 'Processing',
				statusFailed: 'Failed',
				showMore: 'Show more',
				showLess: 'Show less',

				lightningAnalysis: {
					statusProcessingEstimated: 'Processing (est. ~{{estimatedDate}})',
					events: '{{count}} events',
					lightning: 'Lightning',
					dataIncluded: {
						cloudToGround: 'Cloud-to-ground',
						cloudToCloud: 'Cloud-to-cloud',
						strikeDamagePotential: 'Strike damage potential',
						signalStrength: 'Signal strength: {{range}}',
						signalStrengthRangeJoiner: 'or',
						errorEllipseConfidenceLevel: 'Error ellipse confidence level: {{confidence}}%',
					},
				},
				hailAnalysis: {
					hail: 'Hail',
					maxSize: 'Max. size {{size}}',
					noHail: 'No hail observed',
				},
				tabs: {
					ALL: 'All',
					REPEATING: 'Repeating',
				},
				schedule: {
					showReports: 'Show {{count}} reports',
					showReportsPartial: 'Show {{count}}/{{total}} reports',
					hideReports: 'Hide {{count}} reports',
					loadMoreReports: 'Load more reports...',
					nextReportMerge: 'Next report',
					nextReportNldn: 'Next report 2-7 days after {{scheduledDate}}',
					noNextReport: 'No further reports scheduled',
					reportNumber: 'Report #{{number}}',
					statusTag: {
						PAUSED: 'Schedule paused',
						CANCELLED: 'Schedule removed',
						ENDED: 'Schedule ended',
					},
				},
			},

			summaryBanner: {
				selectedArea: '{{radius}} {{unit}} around {{assetName}}{{formattedCustomPoint}}',
				selectedAreaOfSeveralLocations: '{{radius}} {{unit}} around each: {{assetNames}}',
				moreLocations: '+{{hiddenCount}} asset(s)',
			},
			display: {
				reportFilters: {
					title: 'Report filters',
					subtitle: 'Select what data to show',
					none: 'No report filters available',
				},
				views: {
					map: 'Map',
					table: 'Table',
				} satisfies Record<ReportView, string>,
				dataTable: {
					noRows: 'No events match the selected filters',
					loadFailed: 'Failed to load the events. Please try again later.',
					pagination: {
						show: 'Show',
						page: 'Page',
						ofPages_one: 'of {{count}} page',
						ofPages_other: 'of {{count}} pages',
						previousPage: 'Previous page',
						nextPage: 'Next page',
					},
				},
				reportDoesNotExistOrIsNotProcessed: 'Analysis report does not exist or is being processed.',
				download: 'Download',
				downloadFailure: 'Failed to download report. Try again.',
				downloadSuccess: 'Report downloaded',
			},
			lightning: {
				common: {
					summaryBanner: {
						selectedDataset: 'with {{dataset}} dataset',
						scheduleRange: 'Data collected from {{start}} up to {{end}}',
					},
					frequencies: {
						DAILY: 'Daily',
						WEEKLY: 'Weekly',
						MONTHLY: 'Monthly',
					},
					subscriptionEndDate: 'subscription end date',
					datasets: {
						NLDN: 'NLDN reprocessed',
						MERGE: 'Real-time merge',
					} satisfies Record<LightningReportDataset, string>,
					schedule: {
						actions: {
							pauseSchedule: 'Pause',
							resumeSchedule: 'Resume',
							cancelSchedule: 'Delete schedule',
							cancelScheduledReport: 'Delete scheduled report',
						},
						pauseSucceeded: 'Schedule paused',
						pauseFailed: 'Failed to pause schedule',
						resumeSucceeded: 'Schedule resumed',
						resumeFailed: 'Failed to resume schedule',
						cancelSucceeded: 'Schedule deleted',
						cancelFailed: 'Failed to delete schedule',
						cancelReportSucceeded: 'Scheduled report deleted',
						cancelReportFailed: 'Failed to delete scheduled report',

						cancelDialog: {
							titleRepeated: 'Delete schedule',
							titleOneOff: 'Delete scheduled report',
							descriptionRepeated: 'Are you sure you want to delete this schedule? This action cannot be undone.',
							descriptionOneOff: 'Are you sure you want to delete this scheduled report? This action cannot be undone.',
						},
					},
				},
				displayForm: {
					downloadFiltersApplied: 'Download (filters applied)',
					resetFilters: 'Reset filters',
					manageSchedule: 'Manage schedule',
					eventCount: '{{count}} events',
					table: {
						yes: 'Yes',
						no: 'No',
						timeInTimezone: 'Datetime ({{timezone}})',
						clockChange: {
							shiftHours: '{{value}} h',
							shiftMinutes: '{{value}} min',
							back: 'Clocks set back {{shift}} on {{date}} ({{from}} → {{to}}). {{affectedFrom}}–{{affectedTo}} occurs twice, so times show their UTC offset.',
							forward:
								'Clocks set forward {{shift}} on {{date}} ({{from}} → {{to}}). {{affectedFrom}}–{{affectedTo}} does not occur, so times show their UTC offset.',
							multiple: 'Clocks changed {{changes}} times during this report, so each time shows its UTC offset.',
						},
						columns: {
							time: 'Datetime UTC{{offset}}',
							secondsToFault: 'Δt to fault',
							latitude: 'Latitude (°)',
							longitude: 'Longitude (°)',
							cloud: 'Cloud?',
							signalStrengthKA: 'Signal (kA)',
							ellSemiMajM: 'Ell. sma ({{unit}})',
							ellSemiMinM: 'Ell. smi ({{unit}})',
							ellAngleDeg: 'Ell. ang. (°)',
							continuingCurrentProb: 'CCP',
							metalDmgPotentialPctl: 'MDP (%ile)',
							nonMetalDmgPotentialPctl: 'NMDP (%ile)',
							distanceM: 'Distance ({{unit}})',
							closestAssetVrns: 'Closest asset(s)',
							isStrikePtStart: 'SP start?',
							numStrokesInStrike: 'Strokes',
						} satisfies Record<LightningEventColumnKey, string>,
						columnTooltips: {
							time: 'Date and time of the event',
							secondsToFault: 'Time from the fault time to the event. Negative values are before the fault',
							latitude: 'Latitude',
							longitude: 'Longitude',
							cloud: 'Is it a cloud event?',
							signalStrengthKA: 'Signal strength',
							ellSemiMajM: 'Ellipse semi-major axis ({{confidence}}% confidence)',
							ellSemiMinM: 'Ellipse semi-minor axis ({{confidence}}% confidence)',
							ellAngleDeg: 'Elliptic angle',
							continuingCurrentProb: 'Continuing current probability',
							metalDmgPotentialPctl: 'Metal damage potential',
							nonMetalDmgPotentialPctl: 'Non-metal damage potential',
							distanceM: 'Distance to asset',
							closestAssetVrns: 'Asset(s) closest to the event',
							isStrikePtStart: 'Is strike point start?',
							numStrokesInStrike: 'Number of strokes in strike',
						} satisfies Record<LightningEventColumnKey, string>,
					},
					noEvents: {
						title: 'No events',
						subtitle: 'No data to show',
						alert: 'There were no events reported.',
					},
					tooManyEvents: {
						title: 'Too many events',
						subtitle: 'Too many events to display',
						alert:
							'Reports containing over {{maxEventsToDisplayReport, number}} events cannot be visualized here. Please download to view offline.',
					},
					legendTitles: {
						time: 'Lightning events by time',
						CONTINUING_CURRENT_PROBABILITY: 'Lightning events by continuing current probability',
						METAL_DAMAGE_POTENTIAL: 'Lightning events by metal damage potential',
						NON_METAL_DAMAGE_POTENTIAL: 'Lightning events by non-metal damage potential',
					} satisfies Record<StrikeDamagePotentialOption | 'time', string>,
					mapTooltip: {
						strikeDamagePotential: 'Strike damage potential',
						percentile: 'Percentile',
						percentileOrdinal_ordinal_one: '{{count}}st', // 1st, 21st, 31st
						percentileOrdinal_ordinal_two: '{{count}}nd', // 2nd, 22nd, 32nd
						percentileOrdinal_ordinal_few: '{{count}}rd', // 3rd, 23rd, 33rd
						percentileOrdinal_ordinal_other: '{{count}}th', // 4th, 5th, 24th, 11th
					},
					fields: {
						signalStrength: {
							label: 'Signal strength',
							notAvailableSignalStrengthRangeTooNarrow:
								'Filtering based on signal strength is not available because signal strength of the events are within 2 kA of each other',
							excludesAll: 'Selected range excludes all events of the report',
						},
						strikeDamagePotential: {
							label: 'Strike damage potential type',
							showStrikeDamagePotential: 'Show strike damage potential',
							strikeDamagePotentialOptions: {
								CONTINUING_CURRENT_PROBABILITY: 'Continuing current probability',
								METAL_DAMAGE_POTENTIAL: 'Metal damage potential',
								NON_METAL_DAMAGE_POTENTIAL: 'Non-metal damage potential',
							} satisfies Record<StrikeDamagePotentialOption, string>,
							continuingCurrentProbabilities: {
								LOWEST: 'Lowest',
								LOWER: 'Lower',
								MEDIUM: 'Medium',
								HIGHER: 'Higher',
								HIGHEST: 'Highest',
							} satisfies Record<LightningReportContinuingCurrentProbability, string>,
						},
						cloudToCloudEvents: {
							showCloudToCloud: 'Show cloud-to-cloud events',
							hiddenWhenStrikeDamagePotentialEnabled:
								'Cloud-to-cloud events are hidden when strike damage potential visualization is enabled',
						},
						errorEllipses: {
							showErrorEllipses: 'Show error ellipses for each event',
							errorEllipsesEventCountOverThresholdWarning:
								'The report exceeds the threshold of {{threshold}} lightning events to display error ellipses for all events. Select an individual lightning event on the map to see its error ellipse.',
							errorEllipseConfidence: 'Error ellipse confidence',
							tableConfidenceNotice: 'Ellipse values in the table use {{confidence}}% confidence.',
						},
					},
				},
				form: {
					newLightningAnalysisReport: 'New lightning analysis report',
					dataAvailabilityWithFutureInfo:
						'Available from <b>{{startDateTime}}</b> up to <b>{{endDateTime}}</b>. If you need a report for a future time range, you can set it up now. The report will be generated automatically as soon as the required data becomes available.',
					futureOneOffSchedule: 'A one-off scheduled report will be added as soon as data becomes available for <b>{{endDateTime}}</b>',
					futureOneOffScheduleAvailability: 'Currently data is available from <b>{{startDateTime}}</b> up to <b>{{endDateTime}}</b>',
					timeRangeMaxDurationInDaysExceeded_one: 'Maximum supported duration for the chosen geometry is {{count}} day',
					timeRangeMaxDurationInDaysExceeded_other: 'Maximum supported duration for the chosen geometry is {{count}} days',
					timeRangeMinDurationInSecondsNotReached_one: 'Minimum supported duration is {{count}} second',
					timeRangeMinDurationInSecondsNotReached_other: 'Minimum supported duration is {{count}} seconds',
					dateAndBufferOutsideDataAvailability: 'Selected date range is outside of data availability',
					errorEllipsesEnabled: 'Include error ellipses in the report download',
					strikeDamagePotentialEnabled: 'Include strike damage potential information',
					cloudToCloudEventsEnabled: 'Include cloud-to-cloud events',
					signalStrengthFilter: {
						label: 'Include events with signal strength',
						all: 'All',
						positiveOnly: 'Positive',
						custom: 'Custom range',
						invert: 'Invert range selection',
						descriptions: {
							above: 'Events with signal strength above {{signalStrength}} kA are included in the report',
							below: 'Events with signal strength below {{signalStrength}} kA are included in the report',
							between: 'Events with signal strength between {{min}} kA and {{max}} kA are included in the report',
							or: 'Events with signal strength below {{min}} kA or above {{max}} kA are included in the report',
							includesAll: 'Selected range includes all possible signal strength values',
							excludesAll: 'Selected range excludes all possible signal strength values',
						},
					},
					errorEllipsesConfidence: 'Select error ellipse confidence',
					repetition: {
						field: 'Repeat / Schedule',
						options: {
							ONE_OFF: 'Do not repeat',
							REPEATED: 'Repeat',
						},
					},
					repeatedSchedule: {
						period: 'Period',
						startingWeekday: 'Starting weekday',
						weekdays: {
							MON: 'Mon.',
							TUE: 'Tue.',
							WED: 'Wed.',
							THU: 'Thu.',
							FRI: 'Fri.',
							SAT: 'Sat.',
							SUN: 'Sun.',
						},
						startDateLabel: {
							DAILY: 'Repeats daily, with data collected from',
							WEEKLY: 'Repeats weekly, with data collected from',
							MONTHLY: 'Repeats monthly, with data collected from',
						},
						endDateLabel: 'to the final report end date',
						generatedAfterPeriodEnds: 'Report is generated after the period ends',
						info: {
							durationLine: {
								DAILY: '1-day report will be sent daily.',
								WEEKLY: '7-day report will be sent weekly.',
								MONTHLY: '1-month report will be sent monthly.',
							},
							firstReport: 'First report for time range {{firstStart}} - {{firstEnd}} will be added at <b>{{firstAvailable}}</b>',
							firstReportNldn:
								'First report for time range {{firstStart}} - {{firstEnd}} will be added within 2-7 days after <b>{{firstEnd}}</b>',
						},
					},
					faultTimeEntry: 'Fault time entry',
					faultDateTime: 'Fault date-time',
					more: 'More...',
					faultTimeUnit: {
						hour: 'h',
						minute: 'min',
						second: 's',
					},
					durations: {
						min: 'Last {{count}} min',
						hours: 'Last {{count}} h',
						days: 'Last {{count}} days',
					},
					dataset: 'Dataset',
					datasets: {
						NLDN: 'NLDN reprocessed',
						MERGE: 'Real-time merge',
					} satisfies Record<LightningReportDataset, string>,
					nldnReprocessedBoundary: 'NLDN reprocessed boundary',
					geometryOutsideNldnReprocessedArea:
						'Selected geometry falls outside NLDN reprocessed boundaries. NLDN reprocessed is available in the continental United States.',
				},
			},
			hail: {
				form: {
					newHailAnalysisReport: 'New hail analysis report',
					hailCoverageBoundary: 'Historical hail data available',
					outsideHailCoverage:
						'Selected geometry falls outside historical hail data boundaries. Historical hail data is available in the areas shown on the map.',
					durationMinNotReached_one: 'Minimum supported duration is {{count}} hour',
					durationMinNotReached_other: 'Minimum supported duration is {{count}} hours',
					durationMaxExceeded_one: 'Maximum supported duration is {{count}} hour',
					durationMaxExceeded_other: 'Maximum supported duration is {{count}} hours',
				},
			},
		},
		smp: {
			accounts: {
				userSearch: {
					title: 'Search users',
					invalidEmail: 'Invalid email address',
					placeholder: 'Search by user email...',
					search: 'Search',
					noUsers: 'No users found',
					noAccount: 'No account',
					columns: {
						user: 'User',
						account: 'Account',
					},
				},
				title: 'Accounts',
				selectAccount: 'Select an account to view details',
				columns: {
					accountName: 'Account Name',
				},
			},
			accountDetails: {
				stripeAccount: 'Stripe Account',
				addProduct: 'Add subscription',
				pauseSubscriptionSuccess: 'Subscription paused successfully',
				pauseSubscriptionError: 'Failed to pause subscription',
				resumeSubscriptionSuccess: 'Subscription resumed successfully',
				resumeSubscriptionError: 'Failed to resume subscription',
				columns: {
					productPlanStatus: 'Subscription and status',
					accountOwner: 'Account owner',
					characteristics: 'Entitlements',
					planStarts: 'Subscription starts',
					planExpires: 'Subscription expires',
				},
				characteristics: {
					users: 'User(s)',
					devices: 'Device(s)',
					protectedAssets: 'Protected Assets',
					xcastService: 'Xcast Service',
					analyze: 'Analyze',
					serviceUsers: 'API Access',
					mapLayers: 'Map Layers',
					alertApi: 'Alert API',
					webRelays: 'Web Relays',
				},
				subscriptionStatus: {
					trial: 'Trial',
					active: 'Active',
					suspended: 'Suspended',
					canceled: 'Canceled',
				},
				characteristicValues: {
					on: 'On',
					off: 'Off',
				},
				actionMenu: {
					title: 'Actions',
					editSubscription: 'Edit subscription',
					pauseSubscription: 'Pause subscription',
					resumeSubscription: 'Resume subscription',
					impersonate: 'Impersonate',
					stopImpersonating: 'Stop impersonating',
					downloadCSV: 'Download CSV',
				},
			},
			webhooks: {
				webhooks: 'Webhooks',
				secrets: 'Secrets',
				heartbeatStatus: 'Heartbeat status',
				recentInvocations: 'Recent invocations',
				url: 'URL',
				eventTypes: 'Event types',
				secretsTable: {
					showSecretValue: 'Show secret value',
					webhookSecretValue: 'Webhook secret value',
					copySecretValueSuccess: 'Webhook secret value copied',
					alerts: {
						noSecrets: 'Webhook does not have any secrets',
						noCurrentlyValidSecrets: 'Webhook does not have any currently valid secrets',
						allHaveExpiryDate:
							'All secrets have an expiry date. Unless expiry date is modified or new secret is created before the expiry date the webhook will cease to work',
						gapsInValidityRanges: 'There are gaps in secret validity ranges',
						overlappingValidityRanges:
							'Two or more secrets have overlapping validity range. The secret with earlier "Valid from" will be used when invoking webhook',
					},
				},
				heartbeatStateTable: {
					lastChecked: 'Last checked',
					statusSince: 'Status since',
					statusReason: 'Status reason',
					status: {
						OK: 'Healthy',
						NOT_OK: 'Not healthy',
						DISABLED: 'Webhook was disabled',
						NO_ACTIVE_SECRET: 'Webhook did not have active secret',
					},
				},
				invocationsTable: {
					time: 'Time',
					eventType: 'Event type',
					statusInformation: 'Status/Information',
					success: 'Success?',
					retryableError: 'Retryable error?',
					attempt: 'Attempt #',
					notificationId: 'Notification ID',
					notificationRequestId: 'Notification request ID',
					showInvocationPayload: 'Show payload',
					webhookInvocationPayload: 'Webhook invocation payload',
					payloadDescription:
						'Note: this contains the payload sent with send-notification-command, it is missing fields added by the webhook service (eg `eventId`)',
				},
				form: {
					create: 'Create webhook',
					edit: 'Edit webhook',
					details: 'Webhook details',
					invalidUrl: 'Invalid URL, URL must start with https://',
					invalidEventTypes: 'At least one event type must be selected',
					createSuccess: 'Webhook created',
					updateSuccess: 'Webhook updated',
					deleteSuccess: 'Webhook deleted',
					createFailure: 'Failed to create webhook',
					updateFailure: 'Failed to update webhook',
					deleteFailure: 'Failed to delete webhook',
					deleteConfirmTitle: 'Delete webhook',
					deleteConfirmDescription: 'Are you sure you want to delete this webhook? This action cannot be reverted.',
				},
				secretForm: {
					invalidDate: 'Value is not a valid UTC date-time (eg "2025-06-01T23:59:59.999Z")',
					validUntilMustBeInFuture: 'Valid until must be in the future',
					validUntilMustBeBeforeValidFrom: 'Valid until must be after valid from',
					createSuccess: 'Webhook secret created',
					updateSuccess: 'Webhook secret updated',
					deleteSuccess: 'Webhook secret deleted',
					createFailure: 'Failed to create webhook secret',
					updateFailure: 'Failed to update webhook secret',
					deleteFailure: 'Failed to delete webhook secret',
					edit: 'Edit webhook secret',
					create: 'Create webhook secret',
					details: 'Webhook secret details',
					fromNow: 'From now',
					deleteConfirmTitle: 'Delete webhook secret',
					deleteConfirmDescription: 'Are you sure you want to delete this webhook secret? This action cannot be reverted.',
				},
				noExpiry: 'No expiry date',
				validFrom: 'Valid from',
				validUntil: 'Valid until',
				status: 'Status',
				id: 'ID',
				created: 'Created',
				updated: 'Updated',
				create: 'Create',
				edit: 'Edit',
				noWebhooks: 'No webhooks',
				noSecrets: 'No secrets',
				noHeartbeatState: 'No heartbeat state',
				noInvocations: 'No invocations',
			},
			webRelays: {
				failedToTestWebRelay: 'Failed to test web relay',
				testResponse: 'Test response',
				certification: {
					title: 'Certification',
					macAddress: 'Web relay connection information for',
					connectionString: 'Web relay connection string',
					serverName: 'Server name',
					serverPortForProxy1: 'Server port for proxy 1',
					serverPortForProxy2: 'Server port for proxy 2',
					certificateServerPort: 'Certificate server port',
					certificateRequestToken: 'Certificate request token',
					connectionInterval: 'Connection interval',
					failedToCreateCertificate: 'Failed to create certificate',
					fieldCopiedToClipboard: 'Field "{{fieldName}}" copied to clipboard',
					enterMacAddress: 'Enter MAC address',
					generate: 'Generate',
					webRelayType: 'Web relay type',
				},
				statusTable: {
					title: 'Status',
					connected: 'Connected',
					registered: 'Registered',
					since: 'Since',
					host: 'Host',
					port: 'Port',
					proxy: 'Proxy',
					relays: 'Relays',
					test: 'Test',
				},
				registrationsTable: {
					title: 'Registrations',
					asset: 'Asset',
					priority: 'Priority',
					relay: 'Relay',
					health: 'Health',
					proxy: 'Proxy',
					createdAt: 'Created at',
				},
				register: {
					title: 'Register new web relay',
					register: 'Register',
					macAddressAlreadyRegistered: 'Selected MAC address is already registered. Registering will replace the existing registration.',
					macAddressInvalid: 'Invalid MAC address',
					success: 'Web relay registered',
					failure: 'Failed to register web relay',
				},
				delete: {
					title: 'Delete web relay registration',
					message: 'Are you sure you want to delete web relay registration?',
					success: 'Web relay registration deleted',
					failure: 'Failed to delete web relay registration',
				},
				noRegistrations: 'No registrations',
				macAddress: 'MAC address',
			},
			productForm: {
				title: {
					create: 'Add subscription',
					update: 'Update subscription',
				},
				subtitle: {
					create: 'Configure admin user and subscription details for new account',
					update: 'Update subscription details',
				},
				userInfo: {
					title: 'Add User Information',
					subtitle: 'to invite admin user',
					name: 'Name',
					email: 'Email',
					phone: 'Phone',
					next: 'Next',
				},
				subscriptionDetails: {
					title: 'Subscription details',
					subtitle: 'for selected customer account',
					productOffering: 'Product Offering',
					selectProductOffering: 'Select a product offering',
					stripeAccount: 'Stripe Account',
					stripeSubscriptionId: 'Stripe Subscription ID',
					back: 'Back',
				},
				actions: {
					cancel: 'Cancel',
					create: 'Create',
					update: 'Update',
					delete: 'Delete',
					saving: 'Saving...',
					updateSuccess: 'Subscription updated successfully',
					createSuccess: 'Subscription created successfully',
				},
				deleteDialog: {
					title: 'Delete subscription',
					message: 'Are you sure you want to delete subscription {{name}}?',
					deleting: 'Deleting',
				},
				validation: {
					nameRequired: 'Name is required',
					emailInvalid: 'Invalid email address',
					emailExists: 'A user with this email already exists',
					productOfferingRequired: 'Product offering ID is required',
					stripeSubscriptionIdRequired: 'Stripe subscription ID is required',
					valueRequired: 'This value is required',
				},
			},
			searchDetails: {
				device: 'Device',
				component: 'Component',
				id: 'ID',
				lastOnline: 'last online',
				lastRead: 'last read',
				latitude: 'latitude',
				longitude: 'longitude',
				customerOwner: 'Customer owner',
				subscriptionStatus: 'Subscription status',
				subscription: 'Subscription',
				productName: 'Product name',
				startDate: 'Start date',
				endDate: 'End date',
				trial: 'Trial',
				deviceNotFound: 'Device not found',
				status: 'Status',
				serialNumber: 'Serial number',
				model: 'Model',
				firmware: 'Firmware',
				batteryLife: 'Battery life',
				batteryVoltage: 'Battery voltage',
				installationDate: 'Install date',
				copyId: 'Copy ID',
				simOperator: 'sim Operator',
				simIccid: 'SIM ICCID',
				copyIccid: 'Copy ICCID',
				link: 'link',
				portalLink: 'portal link',
				sensorLifetime: {
					durationMonthsDays: '{{months}} mo {{days}} d',
					durationDays: '{{days}} d',
					summary: '{{usedDuration}} used / {{remainingDuration}} remaining lifetime',
				},
				uptimeValue: '{{value}}% uptime',
				uptimeLastThirtyDays: 'Last 30 days',
				uptimeLifetime: 'Lifetime',
				signal: {
					title: 'Signal',
					signalTestFailed: 'Signal test failed',
					signalTestMissing: 'Signal test missing',
					missingValues: 'Missing values',
					allGood: 'All good',
					poorRss: 'Poor RSS',
					poorSnr: 'Poor SNR',
					poorLq: 'Poor LQ',
					lifetimeAvg: 'Lifetime average',
					thirtyDayAvg: '30 day average',
					radioSignalStrength: 'Radio Signal Strength (RSS)',
					signalToNoiseRatio: 'Signal to Noise Ratio (SNR)',
					linkQuality: 'Link Quality (LQ)',
					cellId: 'Cell ID',
					samples: 'Samples',
					location: 'Location',
					timestamp: 'Timestamp',
					result: 'Result',
					pass: 'Pass',
					fail: 'Fail',
					averages: 'Averages',
				},
				recommendedActions: {
					title: 'Recommended action',
					diagnostics: 'Diagnostics',
					noActions: 'No actions required',
					actionReason: 'Reasoning',
					parameterColumn: 'Parameter',
					valueColumn: 'Value',
					referenceColumn: 'Reference',
					explanationColumn: 'Explanation',
					missingMeasurementsHeader: 'Last measurements',
					importantChecksHeader: 'Important checks',
					additionalChecksHeader: 'Additional checks',
					recommendedActionColumn: 'Recommended next best action',
					failureReasonColumn: 'Failure reason',
					suspectColumn: 'Suspect',
					whyProblematicColumn: 'Why problematic',
					yes: 'Yes',
					no: 'No',
					actions: {
						createEcr: 'Create an ECR',
						newSensorNeededSameLocation: 'Replace sensor in same location',
						newSensorNeededRelocate: 'Replace sensor and relocate',
						waitForRecovery: 'Device offline, wait for recovery ({{hours}}h remaining)',
						monitorSnowBlocking: 'Monitor. Likely snow blocking wind measurements',
						hibernationOrCreateEcr: 'Hibernates if temp below -20°C. If not create ECR',
					} satisfies Record<ActionId, string>,
					reasons: {
						online: 'Device online',
						offline: 'Device offline',
						missingMeasurement: 'missing measurement',
						noIssues: 'no issues detected',
						batteryIssue: 'battery issue',
						noBatteryIssue: 'no battery issue',
						ageOver1_5Years: 'device age > 1.5 years',
						ageUnder1_5Years: 'device age ≤ 1.5 years',
						ageUnknown: 'device age unknown',
						locationIssue: 'signal location issue',
						waitingForRecovery: 'offline < 2 days, monitoring for recovery',
						atmoCastWindBlocking: 'AtmoCast wind measurement blocked by snow',
						atmoCastHibernation: 'AtmoCast hibernation below -20°C',
					} satisfies Record<ReasonPart, string>,
					checks: {
						deviceStatus: {
							label: 'Device status',
							whyProblematic: 'Offline devices could have battery or signal issues.',
						},
						voltageAvg30d: {
							label: 'Battery voltage 30-day avg',
							whyProblematic: 'Average battery voltage gives rough indication of the battery state.',
						},
						voltageMin30d: {
							label: 'Battery voltage 30-day min',
							whyProblematic:
								'Small minimum values of the battery voltages during last 30 days indicate that the battery is weak when modem is under load.',
						},
						activeTimeLifetimeSum: {
							label: 'Cumulative modem active time',
							whyProblematic:
								'In good radio conditions the active time increases slower than in poor radio conditions, where lot of repetition is needed.',
						},
						snr30d: {
							label: 'SNR 30-day avg',
							whyProblematic:
								'Measures how much stronger the device signal is compared to background noise, indicates the latest trend. A higher Signal to Noise Ratio (SNR) indicates a better connection with less interference.',
						},
						snrLifetime: {
							label: 'SNR lifetime avg',
							whyProblematic:
								'Measures how much stronger the device signal is compared to background noise, indicates the total lifetime average.',
						},
						rss30d: {
							label: 'RSS 30-day avg',
							whyProblematic:
								'Received Signal Strength (RSS) indicates the strength of the radio signal the device is receiving from the network tower, indicates the latest trend. Weaker signals indicate poor coverage or distance from the tower.',
						},
						rssLifetime: {
							label: 'RSS lifetime avg',
							whyProblematic:
								'Received Signal Strength (RSS) indicates the strength of the radio signal the device is receiving from the network tower, indicates the total lifetime average.',
						},
						lq30d: {
							label: 'Link quality 30-day avg',
							whyProblematic:
								'A composite measure of overall connection quality that factors in signal strength, noise, and interference. Indicates the latest trend.',
						},
						lqLifetime: {
							label: 'Link quality lifetime avg',
							whyProblematic:
								'A composite measure of overall connection quality that factors in signal strength, noise, and interference. Indicates the total lifetime average.',
						},
						cellSwitching: {
							label: 'Cell switch count (per day)',
							whyProblematic:
								'Tracks how many times the device has switched between different network base stations. High cell switch count can reduce battery lifetime.',
						},
						uptime30d: {
							label: 'Uptime last 30 days',
							whyProblematic: 'Uptime measures how many messages are received from the device, indicates the latest trend.',
						},
						uptimeLifetime: {
							label: 'Uptime lifetime',
							whyProblematic: 'Uptime measures how many messages are received from the device, indicates the total lifetime average.',
						},
						missingMeasurement: {
							label: 'Missing expected measurement',
							whyProblematic: 'Missing measurements from online device indicates a need to replace the sensor.',
						},
					} satisfies Record<DeviceCheck['label'], unknown>,
					missingMeasurementExplanations: {
						DRS611_SURFACE_TEMPERATURE:
							'Missing surface temperature indicates that the sensor has been damaged most likely due to resurfacing of the road. Device needs to be replaced.',
						DRS611_BASE_TEMPERATURE:
							'Missing BT indicates moisture ingress typically due to failed sealing during installation. Check replacement need with sales manager.',
						DRS611_GROUND_TEMPERATURE:
							'Missing ground temperature indicates that middle part of sensor is damaged.  Device needs to be replaced.',
						FMP103_SURFACE_TEMPERATURE: 'Missing surface temperature indicates moisture inside the sensor. Device needs to be replaced.',
						CXT530_WIND_SPEED:
							'Snow or ice prevents wind measurements during winter. No action since typically measurements remain when snow or ice disappears. Check sensor during summer.',
						CXT530_WIND_DIRECTION:
							'Snow or ice prevents wind measurements during winter. No action since typically measurements remain when snow or ice disappears. Check sensor during summer.',
						CXT530_HIBERNATION:
							'If ambient temperature falls below -20 C or -4 F, the device enters hibernation battery mode. No action. Wait for the temperature to rise above -20 C or -4 F.',
					} satisfies Record<MissingMeasurementExplanationKey, string>,
				},
			},
			deviceSearch: {
				searching: 'Searching...',
				loadingDevices: 'Loading devices...',
				noDevicesFound: 'No devices found',
				minLengthHint: 'Type at least 3 characters to search',
				title: 'Devices',
				lastRead: 'Last read',
				toggleLabel: 'Devices ({{count}})',
				newFaultsLabel_one: '{{count}} New fault',
				newFaultsLabel_other: '{{count}} New faults',
				noNewFaults: 'No new faults',
				newFaultsFiltersIgnored: "Status and last read filters don't apply to new faults",
				compare: 'Compare {{name}}',
				tabs: {
					all: 'All ({{count}}/{{total}})',
					newFaults: 'New faults ({{count}}/{{total}})',
				},
			},
			legend: {
				statusLabels: {
					OK: 'Online',
					DEGRADED: 'Degraded',
					ERROR: 'Error',
					OFFLINE: 'Offline',
					UNKNOWN: 'Unknown',
				} satisfies Record<DeviceStatus['status'], string>,
			},
			filters: {
				toggleLabel: 'Filters',
				closeLabel: 'Close filters',
				activeCount_one: '{{count}} filter',
				activeCount_other: '{{count}} filters',
				device: 'Device',
				lastRead: 'Last read',
				operator: 'Operator',
				subscription: 'Subscription',
				customer: 'Customer',
				country: 'Country',
				reset: 'Reset filters',
				lastReadBuckets: {
					H_1_11: '1-11 hours ago',
					H_12_23: '12-23 hours ago',
					H_24_35: '24-35 hours ago',
					H_36_47: '36-47 hours ago',
					H_48_168: '48-168 hours ago',
					OVER_1_WEEK: 'More than 1 week ago',
					OVER_3_MONTHS: 'More than 3 months ago',
				},
			},
		},
		impersonation: {
			impersonatingAs: 'Impersonating as {{name}}',
			stop: 'Stop',
			stopImpersonating: 'Stop impersonating',
			selectAccount: 'Select account to impersonate',
		},
		subscription: {
			name: 'Name',
			users: 'Users',
			devices: 'Devices',
			apiKeys: 'API keys',
			protectedAssets: 'Protected assets',
			enabled: 'Enabled',
			disabled: 'Disabled',
			pro: 'Pro',
			premium: 'Premium',
			grids: 'Grids',
			status: {
				active: 'Active',
				trial: 'Trial',
			},
		},
		apiKeys: {
			create: 'Create API key',
			name: 'Name',
			apiKey: 'API key',
			status: 'Status',
			created: 'Created',
			block: 'Block',
			unblock: 'Unblock',
			deleteApiKeyConfirmation: 'Are you sure you want to delete API key {{name}}?',
			copyApiKeySuccess: 'API key copied to clipboard',
			enterNewApiKeyName: 'Enter new API key name',
			createApiKeySuccess: 'API key created successfully',
			createApiKeyFailure: 'Failed to create API key',
			updateApiKeySuccess: 'API key updated successfully',
			updateApiKeyFailure: 'Failed to update API key',
			deleteApiKeySuccess: 'API key deleted successfully',
			deleteApiKeyFailure: 'Failed to delete API key',
		},
		users: {
			actions: 'Actions',
			userCount_one: '{{count}} user',
			userCount_other: '{{count}} users',
			allUsers: 'All users',
			add: 'Add users',
			edit: 'Edit user',
			name: 'Name',
			status: 'Status',
			email: 'Email',
			phone: 'Phone',
			mfa: 'MFA',
			role: 'Role',
			created: 'Created',
			emailUnverified: 'Unverified email',
			resetPassword: 'Reset password',
			resendEmailVerification: 'Resend email verification',
			enableMFA: 'Enable MFA',
			disableMFA: 'Disable MFA',
			passwordResetSuccess: 'Password reset email sent successfully',
			passwordResetError: 'Failed to send password reset email',
			enableMFASuccess: 'MFA enabled successfully',
			enableMFAError: 'Failed to enable MFA',
			disableMFASuccess: 'MFA disabled successfully',
			disableMFAError: 'Failed to disable MFA',
			active: 'Active',
			blocked: 'Blocked',
			blockUser: 'Block user',
			unblockUser: 'Unblock user',
			deleteUser: 'Delete user',
			deleteUserConfirmation: 'Are you sure you want to delete user {{user.fullName}}?',
			invalidEmail: 'Invalid email',
			emailTooLong: 'Email is too long',
			emailInUse: 'Email already in use',
			duplicateEmails: 'Email must be unique',
			couldNotAddEmail: "This email couldn't be added to your subscription. For help, please contact support.",
			notFound: 'User not found',
			userListNotFound: 'User list not found',
			nameTooShort: 'Name is too short',
			nameTooLong: 'Name is too long',
			addAnother: 'Add another user',
			detailsTitle: 'Enter details and define the role.',
			roleDescriptions: {
				USER: 'The <b>User</b> role can view assets and alerts, and edit their own profile.',
				LIMITED_ADMIN:
					'The <b>Admin (Limited)</b> role can add, edit, and delete users and user lists. This role has view-only access to assets, alerts, and other settings.',
				ADMIN: 'The <b>Admin</b> role can add, edit, and delete assets, alerts, users, user lists, and manage other settings.',
			},
			reviewTitle: 'Review the correctness of the information',
			reviewGuide: 'Send the invites, if everything looks good.<br />If you need to make changes, go back to the previous step.',
			sendInvites: 'Send invites',
			roles: {
				ADMIN: 'Admin',
				USER: 'User',
				SUPER_ADMIN: 'Super Admin',
				INTERNAL_READ_ONLY: 'Read Only Internal',
				LIMITED_ADMIN: 'Admin (Limited)',
			},
			formSteps: {
				detailsLabel: 'User details',
				detailsDescription: 'Define details',
				reviewLabel: 'Review',
				reviewDescription: 'Correctness of the information',
				listDetailsLabel: 'List details',
				listDetailsDescription: 'Add users to the list',
			},
			list: {
				add: 'Add user list',
				edit: 'Edit user list',
				deleteModalTitle: 'Delete user list',
				deleteModalConfirmation: 'Are you sure you want to delete the user list <b>{{userList}}</b>?',
				name: 'User list name',
				default: 'Default',
				setAsDefault: 'Set as default user list',
				selectUsers: 'Select users',
				selectUsersSubtitle: 'Select users to add to the list.',
				review: 'Review details and save',
				reviewSubtitle: 'Save if everything looks good.<br />If you need to make changes, go back to the previous step.',
				usersCount_one: '{{count}} user',
				usersCount_other: '{{count}} users',
				userListCreated: 'User list <b>{{userList}}</b> created successfully',
				userListCreateError: 'Failed to create user list',
				userListUpdated: 'User list <b>{{userList}}</b> updated successfully',
				userListUpdateError: 'Failed to update user list',
				userListDeleted: 'User list <b>{{userList}}</b> deleted successfully',
				userListDeleteError: 'Failed to delete user list',
				listNameTooShortError: 'User list name must be at least 2 characters',
				listNameTooLongError: 'User list name must be at most 80 characters',
				listNameDuplicateError: 'User list name must be unique',
				defaultListEmptyError: 'Default user list cannot be empty',
				defaultUserListDescription:
					'Use this setting to make this user list the new default list. (The first list you create becomes the default automatically.)',
				allRoles: 'All roles',
			},
			createFailure: 'Failed to invite user(s)',
			limitReached: 'You have reached the maximum number of users ({{maxUsers}}) allowed in your subscription.',
			addAnotherLimitReached: 'Adding another user would exceed the maximum number of users ({{maxUsers}}) allowed in your subscription.',
			userLimitReached: 'User limit reached. Your subscription allows up to {{maxUsers}} users.',
			updateFailure: 'Failed to update user(s)',
			deleteFailure: 'Failed to delete user(s)',
			createSuccess: 'User(s) invited successfully',
			updateSuccess: 'User updated successfully',
			deleteSuccess: 'User deleted successfully',
			blockSuccess: 'User blocked successfully',
			blockFailure: 'Failed to block user',
			unblockSuccess: 'User unblocked successfully',
			unblockFailure: 'Failed to unblock user',
		},
		sso: {
			userInfoManaged: 'Name, email and phone number are managed by <b>your organization</b>.',
		},
		zod: {
			outOfRange: 'Out of range ({{min}} to {{max}})',
			tooBigName: 'Remove {{count}} character (maximum {{max}} is allowed)',
			tooBigName_other: 'Remove {{count}} characters (maximum {{max}} is allowed)',
			tooManyCharacters: 'Maximum of {{max}} characters is allowed',
			requiredNumberWithRange: 'Please use numbers ({{min}} to {{max}})',
			deviceModelRequired: 'Device model is required',
			serialNumberRequired: 'Serial number is required',
			latitudeRequired: 'Latitude is required',
			longitudeRequired: 'Longitude is required',
			altitudeRequired: 'Altitude is required',
			nameRequired: 'Name is required',
			pointOfInterestRequired: 'Point of interest is required',
			tooManyAssets: 'Select at most {{max}} assets',
			invalidValue: 'Invalid value',
			startTimeRequired: 'Start time is required',
			endTimeRequired: 'End time is required',
			faultDateTimeRequired: 'Fault date-time is required',
		},
	},
	measurementTypes: {
		measurementTypeLabels: {
			'2ND_DOMINANT_PRECIPITATION_TYPE': '2nd dominant precipitation type',
			'3RD_DOMINANT_PRECIPITATION_TYPE': '3rd dominant precipitation type',
			AGE: 'Age',
			AIR_FLOW: 'Air flow',
			AIR_PRESSURE: 'Air pressure',
			AIR_PRESSURE_QFE: 'Air pressure QFE',
			AIR_PRESSURE_QFF: 'Air pressure QFF',
			AIR_PRESSURE_QNH: 'Air pressure QNH',
			AIR_QUALITY_INDEX: 'Air quality index',
			AIR_TEMPERATURE: 'Air temperature',
			ALBEDO: 'Albedo',
			AMBIENT_ILLUMINATION: 'Ambient illumination',
			AREA: 'Area',
			BACKGROUND_LUMINANCE: 'Background luminance',
			BATTERY_VOLTAGE: 'Battery voltage',
			BEARING: 'Bearing',
			BLACK_ICE_FREQUENCY: 'Black ice frequency',
			CARBON_DIOXIDE: 'CO<sub>2</sub>',
			CHEMICAL_AMOUNT: 'Chemical amount',
			CHEMICAL_AMOUNT_NACL: 'Chemical amount NaCl',
			CHEMICAL_CONCENTRATION: 'Chemical concentration',
			CLOUD_ALTITUDE: 'Cloud height',
			CLOUD_COVER: 'Cloud cover',
			COARSE_PARTICLE_MASS_CONCENTRATION: 'PM<sub>10</sub>',
			COARSE_PARTICLE_NUMBER_CONCENTRATION: 'Coarse particle number concentration',
			COURSE: 'Course',
			CO_GAS_CONCENTRATION: 'CO',
			CURRENT: 'Current',
			DENSITY: 'Density',
			DENSITY_ALTITUDE: 'Density altitude',
			DEW_POINT_TEMPERATURE: 'Dew point',
			DIFFUSE_HORIZONTAL_IRRADIATION: 'Diffuse horizontal irradiance',
			DIRECT_NORMAL_IRRADIATION: 'Direct normal irradiance',
			DISCOMFORT_INDEX_MODIFIED: 'Discomfort index modified',
			DISTANCE: 'Distance',
			DOMINANT_POLLUTANT: 'Dominant pollutant',
			EFFECTIVE_TEMPERATURE: 'Effective temperature',
			EVAPORATION: 'Evaporation',
			EVAPOTRANSPIRATION: 'Evapotranspiration',
			EVENT_TIME: 'Event time',
			EXTINCTION_COEFFICIENT: 'Extinction coefficient',
			FINE_PARTICLE_MASS_CONCENTRATION: 'PM<sub>2.5</sub>',
			FINE_PARTICLE_NUMBER_CONCENTRATION: 'Fine particle number concentration',
			FOREST_FIRE_DANGER: 'Forest fire danger',
			FREQUENCY: 'Frequency',
			FROST_POINT_TEMPERATURE: 'Frost point',
			FUEL_HUMIDITY: 'Fuel moisture',
			FUEL_TEMPERATURE: 'Fuel temperature',
			GEOPOTENTIAL_HEIGHT: 'Geopotential height',
			GLOBAL_HORIZONTAL_IRRADIATION: 'Global horizontal irradiance',
			GROUND_SPEED: 'Ground speed',
			H2S_GAS_CONCENTRATION: 'H<sub>2</sub>S',
			HAIL_ACCUMULATION: 'Hail accumulation',
			HAIL_DURATION: 'Hail duration',
			HAIL_INTENSITY: 'Hail intensity',
			HEADING: 'Heading',
			HEAT_INDEX: 'Heat index',
			HISTORIC_DENSITY: 'Historic Density',
			ICE_LAYER_THICKNESS: 'Ice layer thickness',
			INTERNAL_TEMPERATURE: 'Internal temperature',
			LATENT_HEAT_FLUX: 'Latent heat flux',
			LEAF_WETNESS_DURATION: 'Leaf wetness duration',
			LIGHTNING_STRIKES_CLOUD: 'Lightning strikes (cloud)',
			LIGHTNING_STRIKES_DISTANT: 'Lightning strikes (distant)',
			LIGHTNING_STRIKES_NEAR: 'Lightning strikes (near)',
			LIGHTNING_STRIKES_OVERHEAD: 'Lightning strikes (overhead)',
			LIQUIDUS_TEMPERATURE: 'Liquidus temperature',
			LONG_WAVE_DIFFUSE_RADIATION: 'Long-wave diffuse radiation',
			LONG_WAVE_DIRECT_RADIATION: 'Long-wave direct radiation',
			LONG_WAVE_REFLECTED_RADIATION: 'Long-wave reflected radiation',
			LONG_WAVE_TOTAL_RADIATION: 'Long-wave total radiation',
			LUNG_DEPOSITED_SURFACE_AREA: 'Lung-deposited surface area',
			MIXING_LAYER_HEIGHT: 'Mixing layer height',
			MIXING_RATIO: 'Mixing ratio',
			MOMENTUM_FLUX: 'Momentum flux',
			MOST_DOMINANT_PRECIPITATION_TYPE: 'Most dominant precipitation type',
			NET_RADIATION: 'Net radiation',
			NO2_GAS_CONCENTRATION: 'NO<sub>2</sub>',
			NO_GAS_CONCENTRATION: 'NO',
			O3_GAS_CONCENTRATION: 'O<sub>3</sub>',
			PARTICLE_MASS_CONCENTRATION: 'Particle mass concentration',
			PARTICLE_MASS_CONCENTRATION_PM0_1: 'PM<sub>1</sub>',
			PARTICLE_MASS_CONCENTRATION_PM1: 'PM<sub>1</sub>',
			PARTICLE_MASS_CONCENTRATION_PM10: 'PM<sub>10</sub>',
			PARTICLE_MASS_CONCENTRATION_PM2_5: 'PM<sub>2.5</sub>',
			PARTICLE_NUMBER_CONCENTRATION: 'Particle number concentration',
			PARTICLE_NUMBER_CONCENTRATION_PM0_1: 'Particle number concentration (PM<sub>1</sub>)',
			PARTICLE_NUMBER_CONCENTRATION_PM1: 'Particle number concentration (PM<sub>1</sub>)',
			PARTICLE_NUMBER_CONCENTRATION_PM10: 'Particle number concentration (PM<sub>10</sub>)',
			PARTICLE_NUMBER_CONCENTRATION_PM2_5: 'Particle number concentration (PM<sub>2.5</sub>)',
			PAR_RADIATION_GLOBAL: 'PAR radiation (global)',
			PLANE_OF_ARRAY_IRRADIATION: 'Plane of array irradiance',
			PITCH: 'Pitch',
			POWER: 'Power',
			PRECIPITATION_ACCUMULATION: 'Precipitation accumulation',
			PRECIPITATION_INTENSITY: 'Precipitation intensity',
			PRESENT_WEATHER: 'Weather',
			PRESENT_WEATHER_METAR: 'Present weather (METAR)',
			PRESSURE_ALTITUDE: 'Pressure altitude',
			PRESSURE_TENDENCY: 'Pressure tendency',
			PRESSURE_TREND: 'Pressure trend',
			PV_PANEL_SOILING_RATIO: 'Photovoltaic panel soiling ratio',
			PV_PANEL_SOILING_RATIO_2: 'Photovoltaic panel soiling ratio 2',
			PV_PANEL_TEMPERATURE: 'Photovoltaic panel temperature',
			PV_PANEL_TRANSMISSION_LOSS: 'Photovoltaic panel transmission loss',
			PV_PANEL_TRANSMISSION_LOSS_2: 'Photovoltaic panel transmission loss 2',
			PV_REFERENCE_IRRADIATION: 'Photovoltaic panel reference irradiance',
			RAIN_ACCUMULATION: 'Rain accumulation',
			RAIN_DURATION: 'Rain duration',
			RAIN_INTENSITY: 'Rain intensity',
			REAR_PLANE_OF_ARRAY_IRRADIATION: 'In-plane rear-side irradiance',
			REFLECTED_HORIZONTAL_IRRADIATION: 'Reflected global horizontal irradiance',
			REFLECTIVITY_FACTOR: 'Reflectivity factor',
			RELATIVE_HUMIDITY: 'Relative humidity',
			RESISTANCE: 'Resistance',
			ROLL: 'Roll',
			SEA_WATER_DENSITY: 'Sea water density',
			SENSIBLE_HEAT_FLUX: 'Sensible heat flux',
			SHORT_WAVE_DIFFUSE_RADIATION: 'Short-wave diffuse radiation',
			SHORT_WAVE_DIRECT_RADIATION: 'Short-wave direct radiation',
			SHORT_WAVE_REFLECTED_RADIATION: 'Short-wave reflected radiation',
			SHORT_WAVE_TOTAL_RADIATION: 'Short-wave total radiation',
			SNOW_ACCUMULATION: 'Snow accumulation',
			SNOW_DEPTH: 'Snow depth',
			SNOW_DEPTH_TO_REF: 'Snow depth to reference',
			SNOW_DURATION: 'Snow duration',
			SNOW_INTENSITY: 'Snow intensity',
			SNOW_LAYER_THICKNESS: 'Snow layer thickness',
			SO2_GAS_CONCENTRATION: 'SO<sub>2</sub>',
			SOIL_HEAT_FLUX: 'Soil heat flux',
			SOIL_MOISTURE: 'Soil moisture',
			SOIL_TEMPERATURE: 'Soil temperature',
			SOLAR_RADIATION: 'Solar radiation',
			SOLIDUS_TEMPERATURE: 'Solidus temperature',
			STRENGTH: 'Strength',
			SUB_SURFACE_TEMPERATURE: 'Ground temperature',
			SUB_SURFACE_TEMPERATURE_BASE: 'Base temperature',
			SUNSHINE_DURATION: 'Sunshine duration',
			SURFACE_GRIP: 'Surface grip',
			SURFACE_STATE: 'Surface state',
			SURFACE_TEMPERATURE: 'Surface temperature',
			TIDE_HEIGHT: 'Tide height',
			TILT_ANGLE: 'Tilt angle',
			TILT_ANGLE_X: 'X-axis tilt angle',
			TILT_ANGLE_Y: 'Y-axis tilt angle',
			TYPE: 'Type',
			ULTRAFINE_PARTICLE_MASS_CONCENTRATION: 'Ultrafine particle mass concentration',
			ULTRAFINE_PARTICLE_NUMBER_CONCENTRATION: 'Ultrafine particle number concentration',
			UVA_RADIATION: 'UVA radiation',
			UVB_RADIATION: 'UVB radiation',
			UVE_RADIATION: 'UVE radiation',
			UV_DIFFUSE_RADIATION: 'UV diffuse radiation',
			UV_DIRECT_RADIATION: 'UV direct radiation',
			UV_INDEX: 'UV index',
			UV_TOTAL_RADIATION: 'UV total radiation',
			VERTICAL_VISIBILITY: 'Vertical visibility',
			VIBRATION_FREQUENCY: 'Vibration frequency',
			VIBRATION_FREQUENCY_X: 'Vibration frequency (X)',
			VIBRATION_FREQUENCY_Y: 'Vibration frequency (Y)',
			VIBRATION_FREQUENCY_Z: 'Vibration frequency (Z)',
			VISIBILITY: 'Visibility',
			VOLTAGE: 'Voltage',
			VOLUMETRIC_WATER_CONTENT: 'Volumetric water content',
			WATER_CONDUCTIVITY: 'Water conductivity',
			WATER_DEPTH: 'Water depth',
			WATER_FLOW_DIRECTION: 'Water flow direction',
			WATER_FLOW_SPEED: 'Water flow speed',
			WATER_LAYER_THICKNESS: 'Water layer thickness',
			WATER_LEVEL_TO_REF: 'Water level to reference',
			WATER_PRESSURE: 'Water pressure',
			WATER_SALINITY: 'Water salinity',
			WATER_TEMPERATURE: 'Water temperature',
			WATER_VAPOUR_FLUX: 'Water vapor flux',
			WATER_VAPOUR_PRESSURE: 'Water vapor pressure',
			WAVE_COUNT: 'Wave count',
			WAVE_DIRECTION: 'Wave direction',
			WAVE_HEIGHT: 'Wave height',
			WAVE_HEIGHT_UPPER_TENTH: 'Wave height 1/10',
			WAVE_HEIGHT_UPPER_THIRD: 'Wave height 1/3',
			WAVE_PERIOD: 'Wave period',
			WAVE_PERIOD_UPPER_TENTH: 'Wave period 1/10',
			WAVE_PERIOD_UPPER_THIRD: 'Wave period 1/3',
			WET_BULB_TEMPERATURE: 'Wet bulb temperature',
			WIND_CHILL: 'Wind chill',
			WIND_COMPONENT_U: 'Wind component east-west',
			WIND_COMPONENT_V: 'Wind component north-south',
			WIND_COMPONENT_Z: 'Wind component vertical',
			WIND_DIRECTION: 'Wind direction',
			WIND_GUST_DIRECTION: 'Wind gust direction',
			WIND_GUST_SPEED: 'Wind gust',
			WIND_LULL_DIRECTION: 'Wind lull direction',
			WIND_POWER: 'Wind power',
			WIND_RUN: 'Wind run',
			WIND_SIGMA_THETA: 'Wind sigma theta',
			WIND_SPEED: 'Wind speed',
			WIND_SQUALL_SPEED: 'Wind squall speed',
			YAW: 'Yaw',
		} satisfies Record<MeasurementTypeParameter, string>,
		measurementUnitLabels: {
			AMPERES: 'A',
			CANDELAS: 'cd',
			CANDELAS_PER_SQUARE_METRE: 'cd/m<sup>2</sup>',
			CENTIMETRES: 'cm',
			COEFFICIENT: '',
			COULOMBS: 'C',
			COUNT: '',
			CUBIC_METRES: 'm<sup>3</sup>',
			CUBIC_METRES_PER_CUBIC_METRE: '',
			CUBIC_METRES_PER_HOUR: 'm<sup>3</sup>/h',
			DAY: 'd',
			DECIBELS_OF_REFLECTIVITY: 'dBZ',
			DECIBELS_Z: 'dBZ',
			DEGREES: 'deg',
			DEGREES_CELSIUS: '°C',
			DEGREES_FAHRENHEIT: '°F',
			EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_LONG: 'Events/km<sup>2</sup>/year',
			EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_SHORT: 'e/km<sup>2</sup>/yr',
			EVENTS_PER_SQUARE_MILES_PER_YEAR_LONG: 'Events/mi<sup>2</sup>/year',
			EVENTS_PER_SQUARE_MILES_PER_YEAR_SHORT: 'e/mi<sup>2</sup>/yr',
			EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_LONG: 'Events/nmi<sup>2</sup>/year',
			EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_SHORT: 'e/nmi<sup>2</sup>/yr',
			EVENTS_PER_SQUARE_KILOMETRE_PER_HOUR_SHORT: 'e/km<sup>2</sup>/h',
			EVENTS_PER_SQUARE_MILES_PER_HOUR_SHORT: 'e/mi<sup>2</sup>/h',
			EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_HOUR_SHORT: 'e/nmi<sup>2</sup>/hour',
			FOREST_FIRE_DANGER_INDEX: '',
			GRAMS_PER_KILOGRAM: 'g/kg',
			GRAMS_PER_LITRE: 'g/l',
			GRAMS_PER_SQUARE_METRE: 'g/m<sup>2</sup>',
			HECTOPASCALS: 'hPa',
			HECTOPASCALS_PER_HOUR: 'hPa/h',
			HERTZ: 'Hz',
			HOUR: 'h',
			HITS_PER_SQUARE_CENTIMETRE: 'hits/cm<sup>2</sup>',
			HITS_PER_SQUARE_CENTIMETRE_PER_HOUR: 'hits/cm<sup>2</sup>/h',
			IMPERIAL: 'non-metric',
			INCHES: 'in',
			INCHES_PER_HOUR: 'in/h',
			INCH_OF_MERCURY: 'inHg',
			JOULES: 'J',
			KILOAMPERES: 'kA',
			KILOGRAMS: 'kg',
			KILOGRAMS_PER_CUBIC_METRE: 'kg/m<sup>3</sup>',
			KILOGRAMS_PER_KILOGRAM: 'kg/kg',
			KILOGRAMS_PER_METRE: 'kg/m',
			KILOGRAMS_PER_SQUARE_METRE_PER_SECOND: 'kg/m<sup>2</sup>/s',
			KILOMETRES: 'km',
			KILOMETRES_PER_HOUR: 'km/h',
			KILOMETRES_SQUARED: 'km<sup>2</sup>',
			KILOWATTS: 'kW',
			KILOWATT_HOURS: 'kWh',
			KNOTS: 'knots',
			LUMENS: 'lm',
			LUX: 'lx',
			METRES: 'm',
			METRES_PER_SECOND: 'm/s',
			METRES_PER_SECOND_SQUARED: 'm/s<sup>2</sup>',
			METRIC: '',
			MICROGRAMS_PER_CUBIC_METRE: 'μg/m<sup>3</sup>',
			MILES: 'mi',
			MILES_PER_HOUR: 'mph',
			MILES_SQUARED: 'mi<sup>2</sup>',
			MILLIBARS: 'mbar',
			MILLIMETRES: 'mm',
			MILLIMETRES_PER_HOUR: 'mm/h',
			MINUTES: 'min',
			NAUTICAL_MILES: 'nmi',
			NAUTICAL_MILES_SQUARED: 'nmi<sup>2</sup>',
			NEWTONS: 'N',
			NEWTONS_PER_SQUARE_METRE: 'N/m<sup>2</sup>',
			NO_UNIT: '',
			NWS_WEATHER_CODE: '',
			OCTAS: '',
			OHMS: 'Ω',
			ONE_PER_KILOMETRE: '1/km',
			PARTS_PER_BILLION: 'ppb',
			PARTS_PER_MILLION: 'ppm',
			PASCALS: 'Pa',
			PERCENT: '%',
			PERCENTILE: '%ile',
			SECONDS: 's',
			SIEMENS_PER_METRE: 'S/m',
			SQUARE_MICROMETRE_PER_CUBIC_CENTIMETRE: 'μm<sup>2</sup>/cm<sup>3</sup>',
			SURFACE_STATE: '',
			SURFACE_STATE_EN15518_3: '',
			SURFACE_STATE_NTCIP: '',
			SURFACE_STATE_VAISALA: '',
			THOUSANDS_PER_CUBIC_CENTIMETRE: 'thousands/cm<sup>3</sup>',
			UV_INDEX: 'UV Index',
			VOLTS: 'V',
			WATTS: 'W',
			WATTS_PER_SQUARE_METRE: 'W/m<sup>2</sup>',
			WMO_CODE_0200: '',
			WMO_CODE_4678: '',
			WMO_CODE_4680: '',
			WMO_NIL_KIND: '',
			AIR_QUALITY_INDEX: 'AQI',
			USA_EPA_AIR_NOW: 'AirNow',
			EEA_EAQI: 'EAQI',
			USA_EPA_AIR_NOW_POLLUTANT_CODE: '',
			EEA_EAQI_POLLUTANT_CODE: '',
		} satisfies Record<MeasurementTypeUnit, string>,
		measurementStatistic: { MEAN: 'mean', MAXIMUM: 'max.', MINIMUM: 'min.' },
		measurementPeriodLabels: { PT1H: 'last 1h' },
		aqi: {
			usaepaAirNow: {
				good: 'Good',
				moderate: 'Moderate',
				unhealthyForSensitiveGroups: 'Unhealthy for sensitive groups',
				unhealthy: 'Unhealthy',
				veryUnhealthy: 'Very unhealthy',
				hazardous: 'Hazardous',
			},
			eeaEaqi: {
				good: 'Good',
				fair: 'Fair',
				moderate: 'Moderate',
				poor: 'Poor',
				veryPoor: 'Very poor',
				extremelyPoor: 'Extremely poor',
			},
		},
		aqiShort: {
			usaepaAirNow: {
				good: 'Good',
				moderate: 'Mode…',
				unhealthyForSensitiveGroups: 'USG',
				unhealthy: 'Unhe…',
				veryUnhealthy: 'Very…',
				hazardous: 'Haza…',
			},
			eeaEaqi: {
				good: 'Good',
				fair: 'Fair',
				moderate: 'Mode…',
				poor: 'Poor',
				veryPoor: 'Very…',
				extremelyPoor: 'Extr…',
			},
		},
		measurementTypeFullLabels: {
			CO_GAS_CONCENTRATION: 'Carbon monoxide',
			NO2_GAS_CONCENTRATION: 'Nitrogen dioxide',
			NO_GAS_CONCENTRATION: 'Nitrogen monoxide',
			O3_GAS_CONCENTRATION: 'Ozone',
			SO2_GAS_CONCENTRATION: 'Sulfur dioxide',
			H2S_GAS_CONCENTRATION: 'Hydrogen sulfide',
			PARTICLE_MASS_CONCENTRATION_PM1: 'Particle pollution ultrafine',
			PARTICLE_MASS_CONCENTRATION_PM10: 'Particle pollution coarse',
			PARTICLE_MASS_CONCENTRATION_PM2_5: 'Particle pollution fine',
		},
		measurementTypeInfo: {
			mean: '{{duration}} {{unit}} mean at {{height}} meters',
			value: 'Latest value at {{height}} meters',
			sum: '{{duration}} {{unit}} sum at {{height}} meters',
			max: '{{duration}} {{unit}} maximum at {{height}} meters',
			min: '{{duration}} {{unit}} minimum at {{height}} meters',
			unknown: 'Unknown statistical type',
			minute: 'minute',
			hour: 'hour',
		},
		rejected: {
			failed: 'Quality control not passed.',
			DEVICE: 'Marked as invalid by the measurement device.',
			RANGE: 'Range check failed.',
			CROSS: 'Cross check failed.',
			STEP: 'Step check failed.',
			TYPE: 'Type check failed.',
			PERSISTENCE: 'Persistence check failed.',
			UNKNOWN: 'Unknown reason.',
		} satisfies Record<QualityReason | 'failed', string>,
		xCast: {
			AIR_TEMPERATURE_FORECAST: 'Air temperature forecast',
			AIR_TEMPERATURE_90: 'Air temperature 90th percentile',
			AIR_TEMPERATURE_50: 'Air temperature 50th percentile',
			WIND_SPEED_FORECAST: 'Wind speed forecast',
			WIND_GUST_SPEED_FORECAST: 'Wind gust speed forecast',
			WIND_SPEED_80: 'Wind speed 80th percentile',
			WIND_SPEED_50: 'Wind speed 50th percentile',
			WIND_DIRECTION_FORECAST: 'Wind direction forecast',
		},
		directions: {
			N: 'north',
			NNE: 'north-northeast',
			NE: 'northeast',
			ENE: 'east-northeast',
			E: 'east',
			ESE: 'east-southeast',
			SE: 'southeast',
			SSE: 'south-southeast',
			S: 'south',
			SSW: 'south-southwest',
			SW: 'southwest',
			WSW: 'west-southwest',
			W: 'west',
			WNW: 'west-northwest',
			NW: 'northwest',
			NNW: 'north-northwest',
		},
		presentWeather: {
			0: { category: 'No significant weather observed' },
			4: { category: 'Haze, smoke or dust', detail: 'Visibility 1 km or more' },
			5: { category: 'Haze, smoke or dust', detail: 'Visibility less than 1 km' },
			10: { category: 'Mist' },
			20: { category: 'Fog', detail: 'During the preceding hour' },
			21: { category: 'Precipitation', detail: 'During the preceding hour' },
			22: { category: 'Drizzle or snow grains', detail: 'During the preceding hour' },
			23: { category: 'Rain', detail: 'During the preceding hour' },
			24: { category: 'Snow', detail: 'During the preceding hour' },
			25: { category: 'Freezing drizzle or freezing rain', detail: 'During the preceding hour' },
			30: { category: 'Fog' },
			31: { category: 'Fog or ice fog in patches' },
			32: { category: 'Fog or ice fog', detail: 'Has become thinner during the past hour' },
			33: { category: 'Fog or ice fog', detail: 'No appreciable change during the past hour' },
			34: { category: 'Fog or ice fog', detail: 'Has begun or become thicker during the past hour' },
			35: { category: 'Fog', detail: 'Depositing rime' },
			40: { category: 'Precipitation' },
			41: { category: 'Precipitation', detail: 'Slight or moderate' },
			42: { category: 'Precipitation', detail: 'Heavy' },
			50: { category: 'Drizzle' },
			51: { category: 'Drizzle', detail: 'Not freezing, slight' },
			52: { category: 'Drizzle', detail: 'Not freezing, moderate' },
			53: { category: 'Drizzle', detail: 'Not freezing, heavy' },
			54: { category: 'Drizzle', detail: 'Freezing, slight' },
			55: { category: 'Drizzle', detail: 'Freezing, moderate' },
			56: { category: 'Drizzle', detail: 'Freezing, heavy' },
			57: { category: 'Drizzle and rain', detail: 'Slight' },
			58: { category: 'Drizzle and rain', detail: 'Moderate or heavy' },
			60: { category: 'Rain' },
			61: { category: 'Rain', detail: 'Not freezing, slight' },
			62: { category: 'Rain', detail: 'Not freezing, moderate' },
			63: { category: 'Rain', detail: 'Not freezing, heavy' },
			64: { category: 'Rain', detail: 'Freezing, slight' },
			65: { category: 'Rain', detail: 'Freezing, moderate' },
			66: { category: 'Rain', detail: 'Freezing, heavy' },
			67: { category: 'Rain or drizzle and snow', detail: 'Slight' },
			68: { category: 'Rain or drizzle and snow', detail: 'Moderate or heavy' },
			70: { category: 'Snow' },
			71: { category: 'Snow', detail: 'Slight' },
			72: { category: 'Snow', detail: 'Moderate' },
			73: { category: 'Snow', detail: 'Heavy' },
			74: { category: 'Ice pellets', detail: 'Slight' },
			75: { category: 'Ice pellets', detail: 'Moderate' },
			76: { category: 'Ice pellets', detail: 'Heavy' },
			77: { category: 'Snow grains' },
			78: { category: 'Ice crystals' },
			80: { category: 'Shower(s) or intermittent precipitation' },
			81: { category: 'Rain shower(s)', detail: 'Slight' },
			82: { category: 'Rain shower(s)', detail: 'Moderate' },
			83: { category: 'Rain shower(s)', detail: 'Heavy' },
			84: { category: 'Rain shower(s)', detail: 'Violent' },
			85: { category: 'Snow shower(s)', detail: 'Slight' },
			86: { category: 'Snow shower(s)', detail: 'Moderate' },
			87: { category: 'Snow shower(s)', detail: 'Heavy' },
			88: { category: 'Snow pellets' },
			89: { category: 'Hail' },
			unknown: { category: 'Unknown weather code' },
		},
	},
}
